import { useState, useEffect, useMemo } from 'react';
import { 
  Settings, 
  Users, 
  BarChart3, 
  Shield, 
  AlertTriangle, 
  FileText, 
  Eye,
  Edit,
  Trash2,
  Plus,
  UserPlus,
  Crown,
  Activity,
  TrendingUp,
  DollarSign,
  Home,
  CheckCircle,
  XCircle,
  Clock,
  Search,
  Filter,
  Download,
  Bell,
  Globe,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  MessageSquare,
  Archive,
  RotateCcw,
  Upload,
  X,
  Loader2,
  Share,
  UserCheck,
  Megaphone,
  Copy,
  RefreshCw,
  EyeOff,
  Power,
  UserX,
  Server,
  Zap,
  Database,
  Cpu,
  Wifi,
  CircleDollarSign,
  Mail,
  KeyRound,
  LogIn,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  AreaChart, 
  Area,
  Cell,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Separator } from './ui/separator';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { User, PlatformSetting, SystemAlert } from '../types/admin';
import { Property } from '../types/property';
import { toast } from 'sonner';
import { syncAgentProfiles } from '../lib/agents-api';
import { WhatsAppConfig } from './WhatsAppConfig';
import { BrandSettings } from './BrandSettings';
import { ContactSocialSettings } from './ContactSocialSettings';
import { PropertyListingManager } from './PropertyListingManager';
import { StockSharingFeed } from './StockSharingFeed';
import { AdminPropertyManagement } from './AdminPropertyManagement';
import { AdminAnalytics } from './AdminAnalytics';
import { AdminLeadAllocation } from './AdminLeadAllocation';
import { AdminEmailManagement } from './AdminEmailManagement';
import { EconomicIndicators } from './EconomicIndicators';
import { TransferSignings } from './TransferSignings';
import { propertyAPI } from '../lib/property-api';
import { imageUploadAPI } from '../lib/image-upload-api';
import { adminUserAPI, adminSettingsAPI, adminAlertsAPI, adminAuditAPI, adminMetricsAPI, adminAnalyticsAPI } from '../lib/admin-api';
import { sellerAPI } from '../lib/seller-api';
import { sellerPropertyAPI } from '../lib/seller-property-api';
import { seedAdminData } from '../lib/admin-seed';
import { seedJoeBrown } from '../lib/seed-joe-brown';
import { UserProfileTab } from './UserProfileTab';
import { externalSystemAPI, externalAgentsAPI } from '../lib/external-api';
import type { SystemHealthResponse, SystemStatusResponse, CostReportResponse, HealthCheckResult, CostServiceBreakdown } from '../types/external-listing';
import { getContactEmail, loadRuntimeConfig } from '@/config/runtime-config';
import { updateAPIBase } from '../lib/api-fetch';
import { logger } from '../services/logger.service';
import { AdminAgentSync } from './AdminAgentSync';
import { AdminKnowledgeHub } from './AdminKnowledgeHub';
import { AdminReferrals } from './AdminReferrals';

interface AdminHubProps {
  onBackToCatalog: () => void;
}

export function AdminHub({ onBackToCatalog }: AdminHubProps) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [searchTerm, setSearchTerm] = useState('');
  const [profileUser, setProfileUser] = useState<User | null>(null);
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [editingSetting, setEditingSetting] = useState<PlatformSetting | null>(null);
  const [auditSearchTerm, setAuditSearchTerm] = useState('');
  const [auditCurrentPage, setAuditCurrentPage] = useState(1);
  const auditLogsPerPage = 20;

  // Seller management state
  const [selectedSeller, setSelectedSeller] = useState<any>(null);
  const [editingSeller, setEditingSeller] = useState<any>(null);
  const [showEditSellerDialog, setShowEditSellerDialog] = useState(false);
  const [sellerProperties, setSellerProperties] = useState<any[]>([]);
  const [showSellerPropertiesDialog, setShowSellerPropertiesDialog] = useState(false);
  const [showApprovedPropertiesDialog, setShowApprovedPropertiesDialog] = useState(false);
  const [sellers, setSellers] = useState<any[]>([]);
  const [viewingProperty, setViewingProperty] = useState<any>(null);
  const [sellerSearchTerm, setSellerSearchTerm] = useState('');

  // New user form state
  const [newUserForm, setNewUserForm] = useState({
    name: '',
    lastName: '',
    email: '',
    role: '',
    region: '',
    phone: '',
    password: '',
    department: '',
    bio: '',
    commissionRate: '',
  });
  const [showGeneratedPassword, setShowGeneratedPassword] = useState(false);
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);

  // Property management state
  const [properties, setProperties] = useState<Property[]>([]);
  const [allProperties, setAllProperties] = useState<Property[]>([]); // Including archived
  const [isLoadingProperties, setIsLoadingProperties] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingProperty, setEditingProperty] = useState<Property | null>(null);
  const [isCreatingProperty, setIsCreatingProperty] = useState(false);
  const [propertyStatusFilter, setPropertyStatusFilter] = useState<'all' | 'Published' | 'In-Review' | 'Draft' | 'Archived'>('all');
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [propertySearchTerm, setPropertySearchTerm] = useState('');
  
  // Form state for property creation/editing
  const [formData, setFormData] = useState({
    title: '',
    type: 'House' as Property['type'],
    price: '',
    street: '',
    suburb: '',
    city: '',
    province: 'Gauteng',
    postalCode: '',
    bedrooms: '',
    bathrooms: '',
    parking: '',
    erfSize: '',
    floorSize: '',
    description: '',
    amenities: [] as string[],
    publishImmediately: false,
  });
  
  // Image upload state
  const [uploadedImages, setUploadedImages] = useState<Array<{ url: string; path: string; file?: File }>>([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);

  // Server status
  const [isServerOnline, setIsServerOnline] = useState<boolean | null>(null);

  // Backend data state
  const [users, setUsers] = useState<User[]>([]);
  const [platformSettings, setPlatformSettings] = useState<PlatformSetting[]>([]);
  const [systemAlerts, setSystemAlerts] = useState<SystemAlert[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [systemMetrics, setSystemMetrics] = useState<any>(null);
  const [isLoadingAdminData, setIsLoadingAdminData] = useState(true);

  // Chart Data - now fetched from backend
  const [userGrowthData, setUserGrowthData] = useState<any[]>([]);
  const [userPage, setUserPage] = useState(0);
  const USERS_PER_PAGE = 30;
  const [propertyListingsData, setPropertyListingsData] = useState<any[]>([]);
  const [propertyTypesData, setPropertyTypesData] = useState<any[]>([]);
  const [revenueData, setRevenueData] = useState<any[]>([]);
  const [leadSourceData, setLeadSourceData] = useState<any[]>([]);
  const [agentPerformanceData, setAgentPerformanceData] = useState<any[]>([]);
  const [activityData, setActivityData] = useState<any[]>([]);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(true);

  // AWS System Health / Status / Cost state
  const [awsHealth, setAwsHealth] = useState<SystemHealthResponse | null>(null);
  const [awsStatus, setAwsStatus] = useState<SystemStatusResponse | null>(null);
  const [awsCostReport, setAwsCostReport] = useState<CostReportResponse | null>(null);
  const [costPeriod, setCostPeriod] = useState('current_month');
  const [isLoadingHealth, setIsLoadingHealth] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [isLoadingCost, setIsLoadingCost] = useState(false);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [costError, setCostError] = useState<string | null>(null);
  const [lastHealthRefresh, setLastHealthRefresh] = useState<Date | null>(null);

  const pendingProperties = (Array.isArray(properties) ? properties : []).filter(p => p.status === 'In-Review');
  const unacknowledgedAlerts = (Array.isArray(systemAlerts) ? systemAlerts : []).filter(alert => !alert.acknowledged);

  // Audit logs filtering and pagination
  const filteredAuditLogs = (Array.isArray(auditLogs) ? auditLogs : []).filter(log => {
    const searchLower = auditSearchTerm.toLowerCase();
    return (
      (log.userName?.toLowerCase() || '').includes(searchLower) ||
      (log.action?.toLowerCase() || '').includes(searchLower) ||
      (log.resource?.toLowerCase() || '').includes(searchLower) ||
      (log.details?.toLowerCase() || '').includes(searchLower) ||
      (log.ipAddress || '').includes(searchLower)
    );
  });

  const totalAuditPages = Math.ceil(filteredAuditLogs.length / auditLogsPerPage);
  const paginatedAuditLogs = filteredAuditLogs.slice(
    (auditCurrentPage - 1) * auditLogsPerPage,
    auditCurrentPage * auditLogsPerPage
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-green-500/20 text-green-400 border border-green-500/30';
      case 'Inactive': return 'bg-orange-500/20 text-orange-400 border border-orange-500/30';
      case 'Suspended': return 'bg-red-500/20 text-red-400 border border-red-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin': return <Shield className="w-4 h-4 text-blue-500" />;
      case 'agent': return <Users className="w-4 h-4 text-green-500" />;
      case 'marketing': return <Megaphone className="w-4 h-4 text-purple-500" />;
      case 'seller': return <Home className="w-4 h-4 text-orange-500" />;
      case 'external-agent': return <Globe className="w-4 h-4 text-cyan-500" />;
      default: return <Users className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleDisplay = (role: string) => {
    switch (role) {
      case 'admin': return 'Admin';
      case 'agent': return 'Agent';
      case 'marketing': return 'Marketing';
      case 'seller': return 'Seller';
      case 'external-agent': return 'External Agent';
      default: return role;
    }
  };

  // Generate a secure random password
  const generatePassword = () => {
    const uppercase = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lowercase = 'abcdefghjkmnpqrstuvwxyz';
    const numbers = '23456789';
    const special = '!@#$%&*';
    const all = uppercase + lowercase + numbers + special;
    
    let password = '';
    // Ensure at least one of each type
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];
    
    // Fill remaining with random chars (16 total)
    for (let i = 4; i < 16; i++) {
      password += all[Math.floor(Math.random() * all.length)];
    }
    
    // Shuffle the password
    password = password.split('').sort(() => Math.random() - 0.5).join('');
    
    setNewUserForm(prev => ({ ...prev, password }));
    setShowGeneratedPassword(true);
    setPasswordCopied(false);
  };

  // Convert phone number to E.164 format for AWS Cognito
  const formatPhoneToE164 = (phone: string): string => {
    if (!phone) return '';
    
    // Remove all non-digit characters except leading +
    let cleaned = phone.replace(/[^\d+]/g, '');
    
    // If it starts with +, keep it
    if (cleaned.startsWith('+')) {
      return cleaned;
    }
    
    // If it starts with 0 (South African format), convert to +27
    if (cleaned.startsWith('0')) {
      return '+27' + cleaned.substring(1);
    }
    
    // If it's just digits, assume South Africa and add +27
    if (/^\d+$/.test(cleaned)) {
      // If it already looks like it starts with country code (10+ digits), add +
      if (cleaned.length >= 10 && cleaned.startsWith('27')) {
        return '+' + cleaned;
      }
      // Otherwise assume it's a local number without country code
      return '+27' + cleaned;
    }
    
    return cleaned;
  };

  const copyPasswordToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(newUserForm.password);
      setPasswordCopied(true);
      toast.success('Password copied to clipboard');
      setTimeout(() => setPasswordCopied(false), 3000);
    } catch {
      toast.error('Failed to copy password');
    }
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'Error': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'Warning': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'Info': return <Clock className="w-4 h-4 text-blue-500" />;
      case 'Success': return <CheckCircle className="w-4 h-4 text-green-500" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  // Load properties from backend on mount
  useEffect(() => {
    // Ensure runtime config (API_BASE) is loaded before any API calls fire.
    // loadRuntimeConfig() returns the cached config immediately on subsequent calls.
    loadRuntimeConfig().then(() => {
      updateAPIBase();
      checkServerHealth();
      loadAllProperties();
      loadUsers(); // Load users immediately for User Management tab
      loadAnalyticsData();
      loadAwsSystemData();
      loadAdminData(); // Load sellers, seller properties, alerts, audit logs, metrics
    }).catch((err) => {
      console.error('[AdminHub] Config load failed — API calls skipped:', err);
    });
  }, []);

  const checkServerHealth = async () => {
    const isOnline = await propertyAPI.checkHealth();
    setIsServerOnline(isOnline);
    if (!isOnline) {
      console.warn('⚠️ Server is offline - data may not load');
    }
  };

  const loadAllProperties = async () => {
    try {
      setIsLoadingProperties(true);
      // Fetch all pages (admin needs all listings, not just first 30)
      let allListings: any[] = [];
      let page = 1;
      let hasNextPage = true;
      while (hasNextPage) {
        const { listings, pagination } = await propertyAPI.getProperties({ page, pageSize: 100 });
        allListings = [...allListings, ...(listings ?? [])];
        hasNextPage = pagination?.hasNextPage ?? false;
        page++;
      }
      setAllProperties(allListings); // Include archived
      setProperties(allListings.filter((p: any) => p.status !== 'Archived'));

      // Update server status based on whether we got data
      if (allListings.length > 0 && isServerOnline === null) {
        setIsServerOnline(true);
      }
    } catch (error) {
      logger.error('Error loading properties', error as Error, undefined, 'AdminHub');
      setIsServerOnline(false);
    } finally {
      setIsLoadingProperties(false);
    }
  };

  // Load ONLY users from Cognito (for User Management tab)
  const loadUsers = async () => {
    try {
      setIsLoadingAdminData(true);
      console.log('[AdminHub] Loading users from /auth/users...');

      const usersData = await adminUserAPI.getUsers();

      console.log('[AdminHub] ✅ Users loaded:', {
        count: usersData.length,
        users: usersData
      });

      // Normalize: backend returns `groups`, frontend User type uses `cognitoGroups`
      const normalizedUsersData = usersData.map((u: any) => {
        const groups: string[] = u.cognitoGroups || u.groups || [];
        const role = u.role || groups[0] || '';
        const id = u.id || u.userId || u.cognitoUsername || u.sub || u.username || '';
        return { ...u, cognitoGroups: groups, role, id };
      });
      setUsers(normalizedUsersData);
      return normalizedUsersData;
    } catch (error) {
      logger.error('[AdminHub] ❌ Error loading users', error as Error, undefined, 'AdminHub');
      toast.error('Failed to load users from Cognito');
      return [];
    } finally {
      setIsLoadingAdminData(false);
    }
  };

  const loadAdminData = async () => {
    try {
      setIsLoadingAdminData(true);

      // Load all admin data in parallel
      const [usersData, settingsData, alertsData, logsData, metricsData, sellersData, sellerPropertiesData] = await Promise.all([
        adminUserAPI.getUsers().catch(err => { logger.error('Error loading users', err as Error, undefined, 'AdminHub'); return []; }),
        adminSettingsAPI.getSettings().catch(err => { logger.error('Error loading settings', err as Error, undefined, 'AdminHub'); return []; }),
        adminAlertsAPI.getAlerts().catch(err => { logger.error('Error loading alerts', err as Error, undefined, 'AdminHub'); return []; }),
        adminAuditAPI.getAuditLogs().catch(err => { logger.error('Error loading audit logs', err as Error, undefined, 'AdminHub'); return []; }),
        adminMetricsAPI.getMetrics().catch(err => { logger.error('Error loading metrics', err as Error, undefined, 'AdminHub'); return null; }),
        sellerAPI.getSellers().then(data => { console.log('📋 Sellers loaded from API:', data); return data; }).catch(err => { logger.error('Error loading sellers', err as Error, undefined, 'AdminHub'); return []; }),
        sellerPropertyAPI.getSellerProperties().catch(err => { logger.error('Error loading seller properties', err as Error, undefined, 'AdminHub'); return []; }),
      ]);

      console.log('✅ Admin data loaded:', {
        users: usersData.length,
        settings: settingsData.length,
        alerts: alertsData.length,
        logs: logsData.length,
        sellers: sellersData.length,
        sellerProperties: sellerPropertiesData.length
      });

      // Normalize: backend returns `groups`, frontend User type uses `cognitoGroups`
      const normalizedUsers = (Array.isArray(usersData) ? usersData : []).map((u: any) => ({
        ...u,
        cognitoGroups: u.cognitoGroups || u.groups || [],
      }));
      setUsers(normalizedUsers);
      setPlatformSettings(Array.isArray(settingsData) ? settingsData : []);
      setSystemAlerts(Array.isArray(alertsData) ? alertsData : []);
      setAuditLogs(Array.isArray(logsData) ? logsData : []);
      setSystemMetrics(metricsData);
      setSellers(Array.isArray(sellersData) ? sellersData : []);
      setSellerProperties(Array.isArray(sellerPropertiesData) ? sellerPropertiesData : []);
    } catch (error) {
      logger.error('Error loading admin data', error as Error, undefined, 'AdminHub');
      toast.error('Failed to load admin data. Please check your connection and try again.');
    } finally {
      setIsLoadingAdminData(false);
    }
  };

  // ─── AWS System Health / Status / Cost loaders ───

  const loadAwsSystemData = async () => {
    loadAwsHealth();
    loadAwsStatus();
    loadAwsCostReport(costPeriod);
  };

  // Helper to detect IAM auth / not-deployed / permission errors from system endpoints
  const isIamAuthError = (errOrData: any): string | null => {
    const msg = typeof errOrData === 'string' ? errOrData
      : (errOrData?.message || errOrData?.error || String(errOrData || ''));
    if (
      msg.includes('key=value') ||
      msg.includes('IAM authorization') ||
      msg.includes('ENDPOINT_IAM_AUTH') ||
      msg.includes('ENDPOINT_NOT_DEPLOYED') ||
      msg.includes('ENDPOINT_UNREACHABLE') ||
      msg.includes('not be deployed') ||
      msg.includes('not been deployed') ||
      msg.includes('not yet available') ||
      msg.includes('Missing Authentication Token') ||
      msg.includes('cannot be accessed with Cognito') ||
      (errOrData?.isEndpointMissing === true)
    ) {
      return 'This endpoint is not yet available on the AWS API Gateway. It may not have been deployed yet.';
    }
    // AWS permission / config issues (Cost Explorer not enabled, IAM role missing permissions)
    if (
      msg.includes('AWS_PERMISSION_DENIED') ||
      msg.includes('Cost Explorer') ||
      msg.includes('not authorized to perform') ||
      msg.includes('not enabled or you do not have permission') ||
      (errOrData?.errorType === 'AWS_PERMISSION_DENIED')
    ) {
      return msg.includes('Cost Explorer')
        ? 'Cost Explorer is not enabled in your AWS account. Enable it in the AWS Console to view cost data.'
        : (typeof errOrData?.message === 'string' ? errOrData.message : 'Insufficient AWS permissions for this resource.');
    }
    return null;
  };

  const loadAwsHealth = async () => {
    setIsLoadingHealth(true);
    setHealthError(null);
    try {
      const data = await externalSystemAPI.getHealth(true);
      // Check for graceful error response from proxy
      const iamErr = isIamAuthError(data);
      if (iamErr) { setHealthError(iamErr); return; }
      setAwsHealth(data);
      setLastHealthRefresh(new Date());
    } catch (err: any) {
      const iamErr = isIamAuthError(err);
      if (iamErr) { setHealthError(iamErr); return; }
      logger.error('[AdminHub] Failed to load AWS health', err as Error, undefined, 'AdminHub');
      setHealthError(err.message || 'Failed to load health data');
    } finally {
      setIsLoadingHealth(false);
    }
  };

  const loadAwsStatus = async () => {
    setIsLoadingStatus(true);
    setStatusError(null);
    try {
      const data = await externalSystemAPI.getSystemStatus();
      const iamErr = isIamAuthError(data);
      if (iamErr) { setStatusError(iamErr); return; }
      setAwsStatus(data);
    } catch (err: any) {
      const iamErr = isIamAuthError(err);
      if (iamErr) { setStatusError(iamErr); return; }
      logger.error('[AdminHub] Failed to load AWS status', err as Error, undefined, 'AdminHub');
      setStatusError(err.message || 'Failed to load status data');
    } finally {
      setIsLoadingStatus(false);
    }
  };

  const loadAwsCostReport = async (period: string) => {
    setIsLoadingCost(true);
    setCostError(null);
    try {
      const data = await externalSystemAPI.getCostReport(period);
      const iamErr = isIamAuthError(data);
      if (iamErr) { setCostError(iamErr); return; }
      setAwsCostReport(data);
    } catch (err: any) {
      const iamErr = isIamAuthError(err);
      if (iamErr) { setCostError(iamErr); return; }
      logger.error('[AdminHub] Failed to load AWS cost report', err as Error, undefined, 'AdminHub');
      setCostError(err.message || 'Failed to load cost report');
    } finally {
      setIsLoadingCost(false);
    }
  };

  const handleCostPeriodChange = (period: string) => {
    setCostPeriod(period);
    loadAwsCostReport(period);
  };

  const loadAnalyticsData = async () => {
    try {
      setIsLoadingAnalytics(true);
      
      // Load all analytics data in parallel
      const [
        userGrowth,
        revenue,
        propertyListings,
        propertyTypes,
        leadSources,
        agentPerformance,
        activity
      ] = await Promise.all([
        adminAnalyticsAPI.getUserGrowth().catch(err => { logger.error('Error loading user growth', err as Error, undefined, 'AdminHub'); return []; }),
        adminAnalyticsAPI.getRevenue().catch(err => { logger.error('Error loading revenue', err as Error, undefined, 'AdminHub'); return []; }),
        adminAnalyticsAPI.getPropertyListings().catch(err => { logger.error('Error loading property listings', err as Error, undefined, 'AdminHub'); return []; }),
        adminAnalyticsAPI.getPropertyTypes().catch(err => { logger.error('Error loading property types', err as Error, undefined, 'AdminHub'); return []; }),
        adminAnalyticsAPI.getLeadSources().catch(err => { logger.error('Error loading lead sources', err as Error, undefined, 'AdminHub'); return []; }),
        adminAnalyticsAPI.getAgentPerformance().catch(err => { logger.error('Error loading agent performance', err as Error, undefined, 'AdminHub'); return []; }),
        adminAnalyticsAPI.getActivity().catch(err => { logger.error('Error loading activity', err as Error, undefined, 'AdminHub'); return []; }),
      ]);
      
      console.log('✅ Analytics data loaded:', {
        userGrowth: userGrowth.length,
        revenue: revenue.length,
        propertyListings: propertyListings.length,
        propertyTypes: propertyTypes.length,
        leadSources: leadSources.length,
        agentPerformance: agentPerformance.length,
        activity: activity.length
      });
      
      setUserGrowthData(Array.isArray(userGrowth) ? userGrowth : []);
      setRevenueData(Array.isArray(revenue) ? revenue : []);
      setPropertyListingsData(Array.isArray(propertyListings) ? propertyListings : []);
      setPropertyTypesData(Array.isArray(propertyTypes) ? propertyTypes : []);
      setLeadSourceData(Array.isArray(leadSources) ? leadSources : []);
      setAgentPerformanceData(Array.isArray(agentPerformance) ? agentPerformance : []);
      setActivityData(Array.isArray(activity) ? activity : []);
    } catch (error) {
      logger.error('Error loading analytics data', error as Error, undefined, 'AdminHub');
    } finally {
      setIsLoadingAnalytics(false);
    }
  };

  // Handle image upload (supports multiple files)
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Convert FileList to Array
    const filesArray = Array.from(files);
    
    // Validate all files before uploading
    const invalidFiles = filesArray.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 10MB)`);
        return true;
      }
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        return true;
      }
      return false;
    });

    if (invalidFiles.length === filesArray.length) {
      return; // All files are invalid
    }

    const validFiles = filesArray.filter(file => !invalidFiles.includes(file));
    
    setIsUploadingImage(true);
    setUploadProgress({ current: 0, total: validFiles.length });
    
    try {
      const propertyId = editingProperty?.id;
      const uploadedResults: Array<{ url: string; path: string; file: File }> = [];
      
      // Upload files sequentially to show progress
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        setUploadProgress({ current: i + 1, total: validFiles.length });
        
        try {
          const { url, path } = await imageUploadAPI.uploadImage(file, propertyId);
          uploadedResults.push({ url, path, file });
        } catch (error) {
          logger.error(`Error uploading ${file.name}`, error as Error, undefined, 'AdminHub');
          toast.error(`Failed to upload ${file.name}`);
        }
      }
      
      if (uploadedResults.length > 0) {
        setUploadedImages(prev => [...prev, ...uploadedResults]);
        toast.success(`${uploadedResults.length} image${uploadedResults.length > 1 ? 's' : ''} uploaded successfully`);
      }
    } catch (error) {
      logger.error('Error uploading images', error as Error, undefined, 'AdminHub');
      toast.error('Failed to upload images');
    } finally {
      setIsUploadingImage(false);
      setUploadProgress(null);
      // Reset input so the same files can be selected again
      e.target.value = '';
    }
  };

  // Remove image
  const handleRemoveImage = async (index: number) => {
    const image = uploadedImages[index];
    try {
      await imageUploadAPI.deleteImage(image.path);
      setUploadedImages(prev => prev.filter((_, i) => i !== index));
      toast.success('Image removed');
    } catch (error) {
      logger.error('Error removing image', error as Error, undefined, 'AdminHub');
      toast.error('Failed to remove image');
    }
  };

  // Reorder images (move left/right)
  const handleMoveImage = (index: number, direction: 'left' | 'right') => {
    const newIndex = direction === 'left' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= uploadedImages.length) return;
    
    setUploadedImages(prev => {
      const newArray = [...prev];
      [newArray[index], newArray[newIndex]] = [newArray[newIndex], newArray[index]];
      return newArray;
    });
  };

  // Reset form
  const resetPropertyForm = () => {
    setFormData({
      title: '',
      type: 'House',
      price: '',
      street: '',
      suburb: '',
      city: '',
      province: 'Gauteng',
      postalCode: '',
      bedrooms: '',
      bathrooms: '',
      parking: '',
      erfSize: '',
      floorSize: '',
      description: '',
      amenities: [],
      publishImmediately: false,
    });
    setUploadedImages([]);
    setEditingProperty(null);
  };

  // Open create dialog
  const handleCreateProperty = () => {
    resetPropertyForm();
    setIsCreatingProperty(true);
  };

  // Open edit dialog
  const handleEditProperty = (property: Property) => {
    setEditingProperty(property);
    setFormData({
      title: property.title,
      type: property.type,
      price: property.price.toString(),
      street: property.address.street,
      suburb: property.address.suburb,
      city: property.address.city,
      province: property.address.province,
      postalCode: property.address.postalCode,
      bedrooms: property.facts.bedrooms?.toString() || '',
      bathrooms: property.facts.bathrooms?.toString() || '',
      parking: property.facts.parking?.toString() || '',
      erfSize: property.facts.erfSize?.toString() || '',
      floorSize: property.facts.floorSize?.toString() || '',
      description: property.description,
      amenities: property.amenities,
      publishImmediately: property.status === 'Published',
    });
    setUploadedImages(property.media.map(m => ({ url: m.url, path: m.id })));
    setIsCreatingProperty(true);
  };

  // Submit form (create or update)
  const handleSubmitProperty = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.price || !formData.suburb || !formData.city) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (uploadedImages.length === 0) {
      toast.error('Please upload at least one image');
      return;
    }

    setIsSubmitting(true);
    try {
      // F-021: Payload aligned with createListing Lambda schema (POST /listings)
      const propertyData: any = {
        title: formData.title,
        description: formData.description || '',
        propertyType: formData.type,
        listPrice: parseFloat(formData.price),
        location: [formData.suburb, formData.city].filter(Boolean).join(', ') || 'Unknown Location',
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : undefined,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : undefined,
        parking: formData.parking ? parseInt(formData.parking) : undefined,
        erfSize: formData.erfSize ? parseInt(formData.erfSize) : undefined,
        floorSize: formData.floorSize ? parseInt(formData.floorSize) : undefined,
        squareMeters: formData.floorSize ? parseInt(formData.floorSize) : undefined,
        features: Array.isArray(formData.amenities) ? formData.amenities : [],
        images: uploadedImages.map(img => img.url),
        agentId: 'admin',
        status: formData.publishImmediately ? 'Active' : 'Draft',
        mandateType: 'Sale',
      };

      if (editingProperty) {
        await propertyAPI.updateProperty(editingProperty.id, propertyData);
        toast.success('Property updated successfully!');
      } else {
        await propertyAPI.createProperty(propertyData);
        toast.success('Property created successfully!');
      }

      await loadAllProperties();
      setIsCreatingProperty(false);
      resetPropertyForm();
    } catch (error) {
      logger.error('Error saving property', error as Error, undefined, 'AdminHub');
      toast.error('Failed to save property');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Archive property
  const handleArchiveProperty = async (property: Property) => {
    if (!confirm(`Archive "${property.title}"?`)) return;

    try {
      await propertyAPI.archiveProperty(property.id);
      toast.success('Property archived');
      await loadAllProperties();
    } catch (error) {
      logger.error('Error archiving property', error as Error, undefined, 'AdminHub');
      toast.error('Failed to archive property');
    }
  };

  // Restore archived property
  const handleRestoreProperty = async (property: Property) => {
    try {
      await propertyAPI.updateProperty(property.id, { status: 'Draft' });
      toast.success('Property restored');
      await loadAllProperties();
    } catch (error) {
      logger.error('Error restoring property', error as Error, undefined, 'AdminHub');
      toast.error('Failed to restore property');
    }
  };

  // Bulk archive
  const handleBulkArchive = async () => {
    if (selectedProperties.length === 0) return;
    if (!confirm(`Archive ${selectedProperties.length} selected properties?`)) return;

    try {
      await Promise.all(selectedProperties.map(id => propertyAPI.archiveProperty(id)));
      toast.success(`Archived ${selectedProperties.length} properties`);
      setSelectedProperties([]);
      await loadAllProperties();
    } catch (error) {
      logger.error('Error bulk archiving', error as Error, undefined, 'AdminHub');
      toast.error('Failed to archive properties');
    }
  };

  // Toggle property selection
  const handleTogglePropertySelection = (propertyId: string) => {
    setSelectedProperties(prev =>
      prev.includes(propertyId)
        ? prev.filter(id => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  // Filter properties
  const filteredPropertiesByStatus = propertyStatusFilter === 'all'
    ? allProperties
    : allProperties.filter(p => p.status === propertyStatusFilter);

  const filteredAndSearchedProperties = filteredPropertiesByStatus.filter(p =>
    (p.title || '').toLowerCase().includes(propertySearchTerm.toLowerCase()) ||
    (p.address?.suburb || '').toLowerCase().includes(propertySearchTerm.toLowerCase()) ||
    (p.address?.city || '').toLowerCase().includes(propertySearchTerm.toLowerCase())
  );

  const handleApproveProperty = async (propertyId: string) => {
    try {
      // Find the property to get its sellerId
      const property = sellerProperties.find(p => p.id === propertyId);
      if (!property) return;

      const approvedDate = new Date().toISOString();

      // Approve via dedicated endpoint — PUT /seller-properties/{id}/approve
      await sellerPropertyAPI.approveSellerProperty(propertyId);

      // Update the property status to approved in state
      setSellerProperties(prev => prev.map(p => 
        p.id === propertyId 
          ? { ...p, status: 'approved' as const, approvedDate }
          : p
      ));

      // Find the seller and update their approved count
      const seller = sellers.find(s => s.sellerId === property.sellerId || s.id === property.sellerId);
      if (seller) {
        const sellerId = seller.sellerId || seller.id;
        // Update seller's approved count in backend
        await sellerAPI.updateSeller(sellerId, {
          propertiesApproved: (seller.propertiesApproved || 0) + 1
        });

        // Update the seller's approved count in state
        setSellers(prev => prev.map(s =>
          (s.sellerId || s.id) === sellerId
            ? { ...s, propertiesApproved: (s.propertiesApproved || 0) + 1 }
            : s
        ));
      }
      
      toast.success('Property approved and is now live on the platform');
      
      // Log audit
      await adminAuditAPI.createAuditLog({
        user: 'Admin',
        action: 'Approved property',
        resource: `Property ID: ${propertyId}`,
        details: `Approved seller property listing`,
        ipAddress: 'N/A',
      }).catch(err => logger.error('Failed to log audit', err as Error, undefined, 'AdminHub'));
    } catch (error) {
      logger.error('Error approving property', error as Error, undefined, 'AdminHub');
      toast.error('Failed to approve property');
    }
  };

  const handleRejectProperty = async (propertyId: string) => {
    const reason = window.prompt('Please provide a reason for rejection:');
    if (!reason) return;

    try {
      // Reject via dedicated endpoint — PUT /seller-properties/{id}/reject
      await sellerPropertyAPI.rejectSellerProperty(propertyId, reason);

      // Update the property status to rejected in state
      setSellerProperties(prev => prev.map(p => 
        p.id === propertyId 
          ? { ...p, status: 'rejected' as const, rejectionReason: reason }
          : p
      ));
      
      toast.success('Property rejected. Seller has been notified.');
      
      // Log audit
      await adminAuditAPI.createAuditLog({
        user: 'Admin',
        action: 'Rejected property',
        resource: `Property ID: ${propertyId}`,
        details: `Rejected seller property listing. Reason: ${reason}`,
        ipAddress: 'N/A',
      }).catch(err => logger.error('Failed to log audit', err as Error, undefined, 'AdminHub'));
    } catch (error) {
      logger.error('Error rejecting property', error as Error, undefined, 'AdminHub');
      toast.error('Failed to reject property');
    }
  };

  const handleAcknowledgeAlert = (alertId: string) => {
    toast.success('Alert acknowledged');
  };

  const handleCreateUser = async () => {
    console.log('🔵 handleCreateUser called');
    console.log('📝 Form data:', newUserForm);
    
    // Prevent multiple submissions
    if (isCreatingUser) {
      console.log('⚠️ Already creating user, preventing duplicate submission');
      return;
    }
    
    try {
      setIsCreatingUser(true);
      console.log('✅ Validation starting...');
      
      // Validate required fields
      if (!newUserForm.name || !newUserForm.email || !newUserForm.role) {
        console.log('❌ Missing required fields');
        toast.error('Please fill in all required fields: Name, Email, and Role');
        setIsCreatingUser(false);
        return;
      }

      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newUserForm.email)) {
        console.log('❌ Invalid email format');
        toast.error('Please enter a valid email address');
        setIsCreatingUser(false);
        return;
      }

      // Password validation if provided
      if (newUserForm.password) {
        const password = newUserForm.password;
        const errors: string[] = [];

        if (password.length < 8) {
          errors.push('at least 8 characters');
        }
        if (!/[A-Z]/.test(password)) {
          errors.push('one uppercase letter');
        }
        if (!/[a-z]/.test(password)) {
          errors.push('one lowercase letter');
        }
        if (!/[0-9]/.test(password)) {
          errors.push('one number');
        }
        if (!/[^A-Za-z0-9]/.test(password)) {
          errors.push('one special character');
        }

        if (errors.length > 0) {
          console.log('❌ Password validation failed:', errors);
          toast.error(`Password must contain ${errors.join(', ')}`, {
            duration: 6000,
          });
          setIsCreatingUser(false);
          return;
        }
      }

      // Phone validation and formatting if provided
      if (newUserForm.phone && newUserForm.phone.trim()) {
        // Convert to E.164 format
        const formattedPhone = formatPhoneToE164(newUserForm.phone);
        console.log('📞 Phone formatting:', newUserForm.phone, '→', formattedPhone);
        
        // Validate E.164 format: +[country code][number], minimum 10 digits total
        const e164Regex = /^\+[1-9]\d{9,14}$/;
        if (!e164Regex.test(formattedPhone)) {
          console.log('❌ Invalid phone format after conversion');
          toast.error('Please enter a valid phone number (e.g., +27821234567 or 0821234567)', {
            duration: 6000,
          });
          setIsCreatingUser(false);
          return;
        }
        
        // Update form with formatted phone
        newUserForm.phone = formattedPhone;
      }

      const payload: any = { ...newUserForm };
      // Build full display name from first + last name
      if (payload.lastName && payload.lastName.trim()) {
        payload.displayName = `${payload.name.trim()} ${payload.lastName.trim()}`;
      }
      if (!payload.lastName || !payload.lastName.trim()) {
        delete payload.lastName;
      }
      if (!payload.password) {
        delete payload.password; // Let server handle password-less flow
      }
      if (!payload.department) {
        delete payload.department; // Only send if populated
      }
      if (!payload.bio) {
        delete payload.bio; // Only send if populated
      }
      // If phone is empty, delete it from payload
      if (!payload.phone || !payload.phone.trim()) {
        delete payload.phone;
      }
      // Convert commissionRate to number if provided, otherwise remove
      if (payload.commissionRate) {
        payload.commissionRate = parseFloat(payload.commissionRate);
        if (isNaN(payload.commissionRate) || payload.commissionRate < 0 || payload.commissionRate > 100) {
          console.log('❌ Invalid commission rate');
          toast.error('Commission rate must be a number between 0 and 100');
          setIsCreatingUser(false);
          return;
        }
      } else {
        delete payload.commissionRate;
      }
      
      console.log('📤 Sending request to create user...');
      console.log('📦 Payload:', payload);
      
      const newUser = await adminUserAPI.createUser(payload);
      console.log('✅ User created successfully:', newUser);
      
      // Strip internal _meta before adding to state
      const { _meta, ...userForState } = newUser;
      setUsers(prev => [userForState, ...prev]);
      
      if (_meta?.cognitoAlreadyExisted) {
        toast.success(`Profile synced for ${newUserForm.email}. User already existed in Cognito — KV profile created/updated.`, {
          duration: 5000,
        });
      } else if (newUserForm.password) {
        toast.success(`User created successfully! Password has been set for ${newUserForm.email}`, {
          duration: 5000,
        });
      } else {
        toast.success(`User created successfully with a temporary password for ${newUserForm.email}`, {
          duration: 5000,
        });
      }
      setIsCreateUserOpen(false);
      
      // Reset form
      setNewUserForm({
        name: '',
        lastName: '',
        email: '',
        role: '',
        region: '',
        phone: '',
        password: '',
        department: '',
        bio: '',
        commissionRate: '',
      });
      setShowGeneratedPassword(false);
      setPasswordCopied(false);
      
      // Log audit
      await adminAuditAPI.createAuditLog({
        user: 'Admin',
        action: 'Created user',
        resource: `User: ${newUser.name}`,
        details: `Created new ${newUser.role} account`,
        ipAddress: 'N/A',
      }).catch(err => logger.error('Failed to log audit', err as Error, undefined, 'AdminHub'));
    } catch (error: any) {
      logger.error('❌ Error creating user', error as Error, {
        message: error?.message,
        stack: error?.stack,
        response: error?.response,
      }, 'AdminHub');
      
      // Parse and display detailed error message
      let errorMessage = 'Failed to create user';
      
      if (error?.message) {
        const msg = error.message.toLowerCase();
        console.log('🔍 Parsing error message:', msg);
        
        // Check for specific error types
        if (msg.includes('password')) {
          if (msg.includes('uppercase')) {
            errorMessage = 'Password must contain at least one uppercase letter (A-Z)';
          } else if (msg.includes('lowercase')) {
            errorMessage = 'Password must contain at least one lowercase letter (a-z)';
          } else if (msg.includes('number') || msg.includes('digit')) {
            errorMessage = 'Password must contain at least one number (0-9)';
          } else if (msg.includes('special') || msg.includes('symbol')) {
            errorMessage = 'Password must contain at least one special character (!@#$%^&*)';
          } else if (msg.includes('length') || msg.includes('characters')) {
            errorMessage = 'Password must be at least 8 characters long';
          } else if (msg.includes('requirements')) {
            errorMessage = 'Password does not meet requirements: minimum 8 characters, uppercase, lowercase, number, and special character';
          } else {
            errorMessage = `Password error: ${error.message}`;
          }
        } else if (msg.includes('phone')) {
          if (msg.includes('format') || msg.includes('invalid')) {
            errorMessage = 'Invalid phone number format. Please use: +27821234567 or 0821234567 (no spaces or dashes)';
          } else {
            errorMessage = `Phone error: ${error.message}`;
          }
        } else if (msg.includes('already exists') || msg.includes('duplicate') || msg.includes('exists')) {
          errorMessage = `User with email ${newUserForm.email} already exists`;
        } else if (msg.includes('email')) {
          errorMessage = `Invalid email address: ${error.message}`;
        } else if (msg.includes('unauthorized') || msg.includes('forbidden')) {
          errorMessage = 'You do not have permission to create users';
        } else if (msg.includes('network') || msg.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again';
        } else if (msg.includes('cognito')) {
          errorMessage = `Authentication error: ${error.message}`;
        } else {
          // Show the actual error message from the server
          errorMessage = error.message;
        }
      }
      
      console.log('📢 Showing error to user:', errorMessage);
      toast.error(errorMessage, {
        duration: 6000,
      });
    } finally {
      setIsCreatingUser(false);
      console.log('🔵 handleCreateUser finished');
    }
  };

  const handleUpdateUser = async (id: string, userData: any) => {
    try {
      console.log('🌐 handleUpdateUser - Updating user:', id);
      const updatedUser = await adminUserAPI.updateUser(id, userData);
      // Merge the returned user with the existing user data to preserve agent enrichment fields
      setUsers(prev => prev.map(u => u.id === id ? { ...u, ...updatedUser } : u));
      toast.success('User updated successfully');
      
      // Log audit
      await adminAuditAPI.createAuditLog({
        user: 'Admin',
        action: 'Updated user',
        resource: `User: ${updatedUser?.name || userData.name}`,
        details: `Modified user account settings for ${updatedUser?.email || userData.email}`,
        ipAddress: 'N/A',
      }).catch(err => logger.error('Failed to log audit', err as Error, undefined, 'AdminHub'));
    } catch (error: any) {
      logger.error('Error updating user', error as Error, undefined, 'AdminHub');
      toast.error(error?.message || 'Failed to update user');
    }
  };

  const [togglingUserId, setTogglingUserId] = useState<string | null>(null);

  // Set Password dialog state
  const [passwordTargetUser, setPasswordTargetUser] = useState<User | null>(null);
  const [showSetPasswordDialog, setShowSetPasswordDialog] = useState(false);
  const [newPasswordValue, setNewPasswordValue] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isSettingPassword, setIsSettingPassword] = useState(false);

  // Login-As dialog state
  const [loginAsTargetUser, setLoginAsTargetUser] = useState<User | null>(null);
  const [showLoginAsDialog, setShowLoginAsDialog] = useState(false);
  const [loginAsPassword, setLoginAsPassword] = useState('');
  const [showLoginAsPasswordField, setShowLoginAsPasswordField] = useState(false);
  const [isLoggingInAs, setIsLoggingInAs] = useState(false);

  // Delete user confirmation dialog state
  const [deleteTargetUser, setDeleteTargetUser] = useState<User | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const handleToggleUserStatus = async (user: User) => {
    const currentlyActive = user.status === 'Active';
    const newEnabled = !currentlyActive;
    const action = newEnabled ? 'enable' : 'disable';

    if (!window.confirm(
      `Are you sure you want to ${action} ${user.name}?\n\n${
        newEnabled 
          ? 'This will re-enable their Cognito account and they will be able to log in again.'
          : 'This will disable their Cognito account and they will not be able to log in.'
      }`
    )) return;

    try {
      setTogglingUserId(user.id);
      const result = await adminUserAPI.toggleUserStatus(user.id, newEnabled);
      
      // Update local state
      const updatedUser = result.user || { ...user, status: newEnabled ? 'Active' : 'Inactive' };
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, ...updatedUser } : u));
      
      // Show appropriate toast based on Cognito result
      if (result.cognito?.success) {
        toast.success(`${user.name} has been ${newEnabled ? 'enabled' : 'disabled'} in Cognito and locally.`, {
          duration: 4000,
        });
      } else {
        toast.warning(`${user.name} status updated locally to ${newEnabled ? 'Active' : 'Inactive'}. Cognito: ${result.cognito?.message || 'not updated'}`, {
          duration: 6000,
        });
      }

      // Log audit
      await adminAuditAPI.createAuditLog({
        user: 'Admin',
        action: `${newEnabled ? 'Enabled' : 'Disabled'} user`,
        resource: `User: ${user.name} (${user.email})`,
        details: `User account ${newEnabled ? 'enabled' : 'disabled'}. Cognito: ${result.cognito?.message || 'N/A'}`,
        ipAddress: 'N/A',
      }).catch(err => logger.error('Failed to log audit', err as Error, undefined, 'AdminHub'));
    } catch (error: any) {
      logger.error('Error toggling user status', error as Error, undefined, 'AdminHub');
      toast.error(error?.message || `Failed to ${action} user`);
    } finally {
      setTogglingUserId(null);
    }
  };

  const handleDeleteUser = async (id: string) => {
    try {
      const result = await adminUserAPI.deleteUser(id);
      setUsers(prev => prev.filter(u => u.id !== id));
      
      // Show appropriate toast based on result
      if (result?.cognito?.success) {
        toast.success('User removed and disabled in Cognito');
      } else {
        toast.success(`User removed. ${result?.cognito?.message || ''}`);
      }
      
      // Log audit
      await adminAuditAPI.createAuditLog({
        user: 'Admin',
        action: 'Deleted user',
        resource: `User ID: ${id}`,
        details: `Removed user account. KV: ${result?.kvDeleted ? 'deleted' : 'not present'}. ${result?.cognito?.message || ''}`,
        ipAddress: 'N/A',
      }).catch(err => logger.error('Failed to log audit', err as Error, undefined, 'AdminHub'));
    } catch (error: any) {
      logger.error('Error deleting user', error as Error, undefined, 'AdminHub');
      toast.error(error?.message || 'Failed to delete user');
      throw error; // Re-throw so handleDeleteUserConfirmed keeps the dialog open on failure
    }
  };

  const handleSetPassword = async () => {
    if (!passwordTargetUser || !newPasswordValue) return;
    setIsSettingPassword(true);
    try {
      const userId = passwordTargetUser.userId || passwordTargetUser.id || passwordTargetUser.cognitoUsername || passwordTargetUser.email;
      const result = await adminUserAPI.setUserPassword(userId, newPasswordValue);
      toast.success(result.message || `Password updated for ${passwordTargetUser.name}`);
      setShowSetPasswordDialog(false);
      setNewPasswordValue('');
      setPasswordTargetUser(null);

      await adminAuditAPI.createAuditLog({
        user: 'Admin',
        action: 'Set user password',
        resource: `User: ${passwordTargetUser.name} (${passwordTargetUser.email})`,
        details: 'Admin set a new password for the user',
        ipAddress: 'N/A',
      }).catch(err => logger.error('Failed to log audit', err as Error, undefined, 'AdminHub'));
    } catch (error: any) {
      logger.error('Error setting password', error as Error, undefined, 'AdminHub');
      toast.error(error?.message || 'Failed to set password');
    } finally {
      setIsSettingPassword(false);
    }
  };

  const handleLoginAsUser = async () => {
    // TODO: Endpoint /admin/users/login-as does not exist in backend
    // This feature requires a new Lambda to be implemented
    // Feature: Admin impersonation - login as another user for support/debugging
    // This function has been temporarily disabled until the backend endpoint is created
    toast.error('Login as user feature is not yet available. Backend endpoint pending implementation.');
    return;

    /* DISABLED CODE - Uncomment when backend endpoint is implemented
    if (!loginAsTargetUser || !loginAsPassword) return;
    setIsLoggingInAs(true);
    try {
      const result = await adminUserAPI.loginAsUser(loginAsTargetUser.email, loginAsPassword);
      if (result.success && result.tokens) {
        // SECURE IMPERSONATION: Store tokens in sessionStorage with one-time-use key
        // Generate cryptographically secure random key
        const impersonationKey = `imp_${Date.now()}_${Math.random().toString(36).substring(2, 15)}${Math.random().toString(36).substring(2, 15)}`;

        // Store tokens in sessionStorage (not visible in URL/history)
        const tokenPayload = {
          accessToken: result.tokens.accessToken,
          idToken: result.tokens.idToken,
          refreshToken: result.tokens.refreshToken,
          expiresIn: result.tokens.expiresIn,
          email: loginAsTargetUser.email,
          impersonatedBy: 'admin',
          timestamp: Date.now(),
        };

        sessionStorage.setItem(`sp_imp_${impersonationKey}`, JSON.stringify(tokenPayload));

        // URL only contains the key, NOT the tokens
        const newTabUrl = `${window.location.origin}${window.location.pathname}#impersonate-key=${impersonationKey}`;
        window.open(newTabUrl, '_blank');

        // Clean up sessionStorage after 30 seconds (in case tab doesn't open)
        setTimeout(() => {
          sessionStorage.removeItem(`sp_imp_${impersonationKey}`);
        }, 30000);

        toast.success(`Opened new tab logged in as ${loginAsTargetUser.name}`);
        setShowLoginAsDialog(false);
        setLoginAsPassword('');
        setLoginAsTargetUser(null);

        await adminAuditAPI.createAuditLog({
          user: 'Admin',
          action: 'Login as user (impersonation)',
          resource: `User: ${loginAsTargetUser.name} (${loginAsTargetUser.email})`,
          details: 'Admin opened impersonation session in new tab (secure key method)',
          ipAddress: 'N/A',
        }).catch(err => logger.error('Failed to log audit', err as Error, undefined, 'AdminHub'));
      }
    } catch (error: any) {
      logger.error('Error logging in as user', error as Error, undefined, 'AdminHub');
      toast.error(error?.message || 'Failed to login as user');
    } finally {
      setIsLoggingInAs(false);
    }
    */
  };

  const handleDeleteUserConfirmed = async () => {
    if (!deleteTargetUser) return;
    setIsDeletingUser(true);
    try {
      await handleDeleteUser(deleteTargetUser.id);
      setShowDeleteDialog(false);
      setDeleteTargetUser(null);
      setDeleteConfirmText('');
    } catch {
      // Error already handled in handleDeleteUser
    } finally {
      setIsDeletingUser(false);
    }
  };

  const generateRandomPassword = (): string => {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghjkmnpqrstuvwxyz';
    const digits = '23456789';
    const special = '!@#$%&*';
    const all = upper + lower + digits + special;
    let pw = [
      upper[Math.floor(Math.random() * upper.length)],
      lower[Math.floor(Math.random() * lower.length)],
      digits[Math.floor(Math.random() * digits.length)],
      special[Math.floor(Math.random() * special.length)],
    ];
    for (let i = pw.length; i < 16; i++) pw.push(all[Math.floor(Math.random() * all.length)]);
    return pw.sort(() => Math.random() - 0.5).join('');
  };

  const handleUpdateSetting = async (key: string, value: any) => {
    try {
      const updatedSetting = await adminSettingsAPI.updateSetting(key, value);
      setPlatformSettings(prev => {
        const existing = prev.find(s => s.key === key);
        if (existing) {
          return prev.map(s => s.key === key ? updatedSetting : s);
        } else {
          return [...prev, updatedSetting];
        }
      });
      toast.success('Setting updated successfully');
      setEditingSetting(null);
    } catch (error) {
      logger.error('Error updating setting', error as Error, undefined, 'AdminHub');
      toast.error('Failed to update setting');
    }
  };

  const handleResolveAlert = async (id: string) => {
    try {
      await adminAlertsAPI.updateAlert(id, { resolved: true });
      setSystemAlerts(prev => prev.map(a => a.id === id ? { ...a, resolved: true } : a));
      toast.success('Alert resolved');
    } catch (error) {
      logger.error('Error resolving alert', error as Error, undefined, 'AdminHub');
      toast.error('Failed to resolve alert');
    }
  };

  const [isSeedingData, setIsSeedingData] = useState(false);

  const handleSeedData = async () => {
    try {
      setIsSeedingData(true);
      const success = await seedAdminData();
      if (success) {
        toast.success('Admin data seeded successfully! All 8 users including Lisa Martinez (Marketing) & Thabo Mokoena (Super Admin) loaded.', {
          duration: 5000,
        });
        await Promise.all([loadAdminData(), loadAnalyticsData()]); // Reload all data
      } else {
        toast.error('Failed to seed admin data');
      }
    } catch (error) {
      logger.error('Error seeding data', error as Error, undefined, 'AdminHub');
      toast.error('Failed to seed admin data');
    } finally {
      setIsSeedingData(false);
    }
  };

  const handleSeedJoeBrown = async () => {
    try {
      await seedJoeBrown();
      toast.success('Joe Brown and 3 properties created successfully!');
      await Promise.all([loadAdminData(), loadAnalyticsData()]); // Reload all data
    } catch (error) {
      logger.error('Error seeding Joe Brown', error as Error, undefined, 'AdminHub');
      toast.error('Failed to create Joe Brown');
    }
  };

  // Compute region performance from real user and property data
  const regionPerformance = useMemo(() => {
    const regionMap: Record<string, { agents: number; properties: number; sales: number; revenue: number }> = {};
    
    (Array.isArray(users) ? users : []).forEach(user => {
      const region = user.region || 'Unassigned';
      if (!regionMap[region]) {
        regionMap[region] = { agents: 0, properties: 0, sales: 0, revenue: 0 };
      }
      if (user.role === 'Agent') {
        regionMap[region].agents += 1;
        regionMap[region].properties += user.propertiesManaged || 0;
        regionMap[region].sales += user.salesCompleted || 0;
        regionMap[region].revenue += user.commission || user.revenue || 0;
      }
    });

    return Object.entries(regionMap)
      .filter(([_, data]) => data.agents > 0)
      .map(([region, data]) => ({ region, ...data }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [users]);

  const filteredUsers = (Array.isArray(users) ? users : []).filter(user => {
    const term = searchTerm.toLowerCase();
    return (
      (user.name || '').toLowerCase().includes(term) ||
      (user.email || '').toLowerCase().includes(term) ||
      (user.role || '').toLowerCase().includes(term) ||
      (user.cognitoGroups || []).some((g: string) => g.toLowerCase().includes(term)) ||
      (user.authProvider || '').toLowerCase().includes(term) ||
      (user.cognitoUsername || '').toLowerCase().includes(term) ||
      (user.jobTitle || '').toLowerCase().includes(term) ||
      (user.bio || '').toLowerCase().includes(term)
    );
  });

  if (isLoadingAdminData && users.length === 0) {
    return (
      <div className="container mx-auto px-4 pt-20 pb-8 flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-[#FDB022]" />
          <p className="text-muted-foreground">Loading admin data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 pt-20 pb-8 overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="mb-2">Admin Hub</h1>
          <p className="text-muted-foreground">
            Manage platform users, settings, and monitor system performance
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {isServerOnline === true && (
            <Button variant="outline" onClick={handleSeedJoeBrown} data-testid="admin-seed-joe-brown">
              <Plus className="w-4 h-4 mr-2" />
              Add Joe Brown + 3 Properties
            </Button>
          )}
          <Button variant="outline" onClick={onBackToCatalog} data-testid="admin-view-public-site">
            <Eye className="w-4 h-4 mr-2" />
            View Public Site
          </Button>
          {unacknowledgedAlerts.length > 0 && (
            <Button variant="outline" className="relative" data-testid="admin-view-alerts">
              <Bell className="w-4 h-4 mr-2" />
              Alerts
              <Badge className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 text-xs">
                {unacknowledgedAlerts.length}
              </Badge>
            </Button>
          )}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <div className="overflow-x-auto w-full pb-1">
        <TabsList className="min-w-max">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="analytics">
            <TrendingUp className="w-4 h-4 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="users">User Management</TabsTrigger>
          <TabsTrigger value="agents">Agents</TabsTrigger>
          <TabsTrigger value="property-management">
            <Home className="w-4 h-4 mr-2" />
            Property Management
          </TabsTrigger>
          <TabsTrigger value="leads">
            <UserCheck className="w-4 h-4 mr-2" />
            Lead Allocation
          </TabsTrigger>
          <TabsTrigger value="sellers">Seller Management</TabsTrigger>
          <TabsTrigger value="listings">
            <Globe className="w-4 h-4 mr-2" />
            External Listings
          </TabsTrigger>
          <TabsTrigger value="stock-sharing">
            <MessageSquare className="w-4 h-4 mr-2" />
            Stock Sharing
          </TabsTrigger>
          <TabsTrigger value="signings">
            <FileText className="w-4 h-4 mr-2" />
            Signings
          </TabsTrigger>
          <TabsTrigger value="email-management">
            <Mail className="w-4 h-4 mr-2" />
            Email Management
          </TabsTrigger>
          <TabsTrigger value="whatsapp">WhatsApp Config</TabsTrigger>
          <TabsTrigger value="settings">Platform Settings</TabsTrigger>
          <TabsTrigger value="alerts">System Alerts</TabsTrigger>
          <TabsTrigger value="logs">Audit Logs</TabsTrigger>
          <TabsTrigger value="agent-sync">Agent Sync</TabsTrigger>
          <TabsTrigger value="knowledge-hub" data-testid="adminHub-tab-knowledge-hub">Knowledge Hub</TabsTrigger>
          <TabsTrigger value="referrals" data-testid="adminHub-tab-referrals">Referrals</TabsTrigger>
        </TabsList>
        </div>

        <TabsContent value="dashboard" className="space-y-6">
          {/* System Metrics */}
          <div>
            <h3 className="mb-4">System Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card data-testid="analytics-admin-total-users-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" data-testid="analytics-admin-total-users-label">Total Users</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="analytics-admin-total-users-count">{users.length}</div>
                  <p className="text-xs text-green-600 flex items-center gap-1" data-testid="analytics-admin-total-users-change">
                    <ArrowUp className="h-3 w-3" />
                    {users.filter(u => u.status === 'Active').length} active
                  </p>
                  <p className="text-xs text-muted-foreground mt-1" data-testid="analytics-admin-active-agents-count">
                    {users.filter(u => (u.cognitoGroups || []).includes('agent') || u.role === 'Agent').length} agents · {users.filter(u => u.status === 'CONFIRMED' || u.status === 'Active').length} confirmed
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="analytics-admin-total-properties-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" data-testid="analytics-admin-total-properties-label">Total Properties</CardTitle>
                  <Home className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="analytics-admin-total-properties-count">{systemMetrics?.properties?.total ?? properties.length}</div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="analytics-admin-total-properties-change">
                    {systemMetrics?.properties?.active ?? 0} active · {systemMetrics?.properties?.sold ?? 0} sold
                  </p>
                  <p className="text-xs text-muted-foreground mt-1" data-testid="analytics-admin-pending-approval-count">
                    {systemMetrics?.properties?.underOffer ?? properties.filter(p => p.status === 'In-Review').length} under offer
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="analytics-admin-platform-views-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" data-testid="analytics-admin-platform-views-label">Platform Activity</CardTitle>
                  <Eye className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="analytics-admin-platform-views-count">
                    {activityData.reduce((sum: number, d: any) => sum + (d.views || 0), 0).toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="analytics-admin-platform-views-change">
                    {activityData.find((d: any) => d.time === 'Inquiries')?.leads || 0} inquiries · {activityData.find((d: any) => d.time === 'Appointments')?.views || 0} appointments
                  </p>
                  <p className="text-xs text-muted-foreground mt-1" data-testid="analytics-admin-leads-generated-count">
                    {activityData.find((d: any) => d.time === 'Active Users')?.views || 0} active users this period
                  </p>
                </CardContent>
              </Card>

              <Card data-testid="analytics-admin-revenue-card">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium" data-testid="analytics-admin-revenue-label">Revenue</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold" data-testid="analytics-admin-revenue-value">
                    R{(revenueData.reduce((sum: number, d: any) => sum + (d.revenue ?? 0), 0) / 1000000).toFixed(1)}M
                  </div>
                  <p className="text-xs text-muted-foreground flex items-center gap-1" data-testid="analytics-admin-revenue-change">
                    From deals, commissions & transfers
                  </p>
                  <p className="text-xs text-muted-foreground mt-1" data-testid="analytics-admin-sales-completed-count">
                    {systemMetrics?.properties?.sold || 0} properties sold
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Charts Row 1: User Growth & Revenue */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>User Growth & Agent Acquisition</CardTitle>
                <p className="text-sm text-muted-foreground">7-month trend analysis</p>
              </CardHeader>
              <CardContent>
                {isLoadingAnalytics ? (
                  <div className="flex items-center justify-center h-[300px]">
                    <Loader2 className="w-8 h-8 animate-spin text-[#FDB022]" />
                  </div>
                ) : userGrowthData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={userGrowthData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="date" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1a1a1a', 
                          border: '1px solid #333',
                          borderRadius: '8px'
                        }} 
                      />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="users" 
                        stroke="#FDB022" 
                        strokeWidth={2}
                        name="Total Users"
                        dot={{ fill: '#FDB022', r: 4 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="agents" 
                        stroke="#666666" 
                        strokeWidth={2}
                        name="Active Agents"
                        dot={{ fill: '#666666', r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Revenue Breakdown</CardTitle>
                <p className="text-sm text-muted-foreground">Revenue from deals, commissions & transfers (ZAR)</p>
              </CardHeader>
              <CardContent>
                {isLoadingAnalytics ? (
                  <div className="flex items-center justify-center h-[300px]">
                    <Loader2 className="w-8 h-8 animate-spin text-[#FDB022]" />
                  </div>
                ) : revenueData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No data available
                  </div>
                ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={revenueData}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#FDB022" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#FDB022" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="month" stroke="#666" />
                    <YAxis stroke="#666" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1a1a1a', 
                        border: '1px solid #333',
                        borderRadius: '8px'
                      }} 
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#FDB022" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorRevenue)"
                      name="Revenue (M)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 2: Property Listings & Types */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Property Listings by Type</CardTitle>
                <p className="text-sm text-muted-foreground">Active listings vs. sold properties by property type</p>
              </CardHeader>
              <CardContent>
                {isLoadingAnalytics ? (
                  <div className="flex items-center justify-center h-[300px]">
                    <Loader2 className="w-8 h-8 animate-spin text-[#FDB022]" />
                  </div>
                ) : propertyListingsData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={propertyListingsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="region" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1a1a1a', 
                          border: '1px solid #333',
                          borderRadius: '8px'
                        }} 
                      />
                      <Legend />
                      <Bar dataKey="listings" fill="#FDB022" name="Active Listings" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="sold" fill="#666666" name="Sold Properties" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Property Types</CardTitle>
                <p className="text-sm text-muted-foreground">Distribution by type</p>
              </CardHeader>
              <CardContent>
                {isLoadingAnalytics ? (
                  <div className="flex items-center justify-center h-[300px]">
                    <Loader2 className="w-8 h-8 animate-spin text-[#FDB022]" />
                  </div>
                ) : propertyTypesData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={propertyTypesData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {propertyTypesData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1a1a1a', 
                        border: '1px solid #333',
                        borderRadius: '8px'
                      }} 
                    />
                  </PieChart>
                </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 3: Lead Sources & Agent Performance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Lead Sources</CardTitle>
                <p className="text-sm text-muted-foreground">Where your leads come from</p>
              </CardHeader>
              <CardContent>
                {isLoadingAnalytics ? (
                  <div className="flex items-center justify-center h-[300px]">
                    <Loader2 className="w-8 h-8 animate-spin text-[#FDB022]" />
                  </div>
                ) : leadSourceData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={leadSourceData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis type="number" stroke="#666" />
                      <YAxis dataKey="source" type="category" stroke="#666" width={120} />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1a1a1a', 
                          border: '1px solid #333',
                          borderRadius: '8px'
                        }} 
                      />
                      <Bar dataKey="leads" fill="#FDB022" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Top Performing Agents</CardTitle>
                <p className="text-sm text-muted-foreground">Sales & commission (K)</p>
              </CardHeader>
              <CardContent>
                {isLoadingAnalytics ? (
                  <div className="flex items-center justify-center h-[300px]">
                    <Loader2 className="w-8 h-8 animate-spin text-[#FDB022]" />
                  </div>
                ) : agentPerformanceData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={agentPerformanceData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="name" stroke="#666" />
                      <YAxis stroke="#666" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1a1a1a', 
                          border: '1px solid #333',
                          borderRadius: '8px'
                        }} 
                      />
                      <Legend />
                      <Bar dataKey="sales" fill="#FDB022" name="Sales Closed" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="commission" fill="#666666" name="Commission (K)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* 24-Hour Activity */}
          <Card>
            <CardHeader>
              <CardTitle>Platform Activity (24-Hour)</CardTitle>
              <p className="text-sm text-muted-foreground">Views and leads throughout the day</p>
            </CardHeader>
            <CardContent>
              {isLoadingAnalytics ? (
                <div className="flex items-center justify-center h-[250px]">
                  <Loader2 className="w-8 h-8 animate-spin text-[#FDB022]" />
                </div>
              ) : activityData.length === 0 ? (
                <div className="flex items-center justify-center h-[250px] text-muted-foreground">
                  No data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={activityData}>
                    <defs>
                      <linearGradient id="colorViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#FDB022" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#FDB022" stopOpacity={0.05}/>
                    </linearGradient>
                    <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#666666" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#666666" stopOpacity={0.05}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="time" stroke="#666" />
                  <YAxis stroke="#666" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1a1a1a', 
                      border: '1px solid #333',
                      borderRadius: '8px'
                    }} 
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="views" 
                    stroke="#FDB022" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorViews)"
                    name="Page Views"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="leads" 
                    stroke="#666666" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorLeads)"
                    name="Leads Generated"
                  />
                </AreaChart>
              </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Performance Metrics Summary */}
          <div>
            <h3 className="mb-4">Quick Performance Metrics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{systemMetrics?.avgResponseTime || '2.4h'}</div>
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <ArrowDown className="h-3 w-3" />
                    -18% faster than average
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Agent response to leads
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Platform Uptime</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{systemMetrics?.platformUptime || '99.9%'}</div>
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Excellent performance
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Last 30 days
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">14.5%</div>
                  <p className="text-xs text-green-600 flex items-center gap-1">
                    <ArrowUp className="h-3 w-3" />
                    +3.2% from last month
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Lead to sale conversion
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* External Listings Overview */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3>External Listing Platforms</h3>
              <Button variant="outline" size="sm" onClick={() => setActiveTab('listings')} data-testid="admin-manage-listings">
                <Globe className="w-4 h-4 mr-2" />
                Manage Listings
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Property24</CardTitle>
                  <div className="text-xl">🏠</div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">12</div>
                  <p className="text-xs text-muted-foreground">
                    Properties published
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Private Property</CardTitle>
                  <div className="text-xl">🏡</div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">8</div>
                  <p className="text-xs text-muted-foreground">
                    Properties published
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">ImmoAfrica</CardTitle>
                  <div className="text-xl">🌍</div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">5</div>
                  <p className="text-xs text-muted-foreground">
                    Properties published
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Regional Performance */}
          <Card>
            <CardHeader>
              <CardTitle>Regional Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Region</TableHead>
                    <TableHead>Agents</TableHead>
                    <TableHead>Properties</TableHead>
                    <TableHead>Sales</TableHead>
                    <TableHead>Revenue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {regionPerformance.length > 0 ? regionPerformance.map((region) => (
                    <TableRow key={region.region}>
                      <TableCell className="font-medium">{region.region}</TableCell>
                      <TableCell>{region.agents}</TableCell>
                      <TableCell>{region.properties}</TableCell>
                      <TableCell>{region.sales}</TableCell>
                      <TableCell>R{((Number(region.revenue) || 0) / 1000000).toFixed(1)}M</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No region data available. Seed admin data to populate.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="property-management" className="space-y-6">
          <AdminPropertyManagement
            properties={filteredAndSearchedProperties}
            isLoading={isLoadingProperties}
            statusFilter={propertyStatusFilter}
            searchTerm={propertySearchTerm}
            selectedProperties={selectedProperties}
            formData={formData}
            uploadedImages={uploadedImages}
            isUploadingImage={isUploadingImage}
            uploadProgress={uploadProgress}
            isSubmitting={isSubmitting}
            editingProperty={editingProperty}
            isCreatingProperty={isCreatingProperty}
            onStatusFilterChange={setPropertyStatusFilter}
            onSearchChange={setPropertySearchTerm}
            onCreateProperty={handleCreateProperty}
            onEditProperty={handleEditProperty}
            onArchiveProperty={handleArchiveProperty}
            onRestoreProperty={handleRestoreProperty}
            onToggleSelection={handleTogglePropertySelection}
            onBulkArchive={handleBulkArchive}
            onFormChange={setFormData}
            onImageUpload={handleImageUpload}
            onRemoveImage={handleRemoveImage}
            onMoveImage={handleMoveImage}
            onSubmit={handleSubmitProperty}
            onCloseDialog={() => {
              setIsCreatingProperty(false);
              resetPropertyForm();
            }}
            onPropertiesChanged={loadAllProperties}
          />
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics">
          <AdminAnalytics />
        </TabsContent>

        {/* Lead Allocation Tab */}
        <TabsContent value="leads">
          <AdminLeadAllocation agents={users.filter(u => (u.cognitoGroups || []).includes('agent') || u.role === 'Agent').map(u => ({ id: u.userId, name: u.name, email: u.email }))} />
        </TabsContent>

        <TabsContent value="users" className="space-y-6">
          {/* User Management Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3>User Management</h3>
              <p className="text-muted-foreground">
                Manage Cognito users and their permissions · <span className="text-primary">{users.length} users</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  toast.info('Loading users from Cognito...');
                  const loadedUsers = await loadUsers();
                  toast.success(`User list refreshed — ${loadedUsers.length} users loaded`);
                }}
                data-testid="admin-users-refresh"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            <Dialog open={isCreateUserOpen} onOpenChange={setIsCreateUserOpen}>
              <DialogTrigger asChild>
                <Button data-testid="admin-users-create">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New User</DialogTitle>
                  <DialogDescription>
                    Add a new user to the platform
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>First Name</Label>
                      <Input 
                        placeholder="John" 
                        value={newUserForm.name}
                        onChange={(e) => setNewUserForm(prev => ({ ...prev, name: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name</Label>
                      <Input 
                        placeholder="Doe" 
                        value={newUserForm.lastName}
                        onChange={(e) => setNewUserForm(prev => ({ ...prev, lastName: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input 
                      type="email" 
                      placeholder="john@somethingproperty.co.za" 
                      value={newUserForm.email}
                      onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Role</Label>
                      <Select
                        value={newUserForm.role}
                        onValueChange={(value) => setNewUserForm(prev => ({ ...prev, role: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="agent">Agent</SelectItem>
                          <SelectItem value="marketing">Marketing</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="external-agent">External Agent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Region</Label>
                      <Select 
                        value={newUserForm.region}
                        onValueChange={(value) => setNewUserForm(prev => ({ ...prev, region: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select region" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Cape Town">Cape Town</SelectItem>
                          <SelectItem value="Johannesburg">Johannesburg</SelectItem>
                          <SelectItem value="Durban">Durban</SelectItem>
                          <SelectItem value="Pretoria">Pretoria</SelectItem>
                          <SelectItem value="Port Elizabeth">Port Elizabeth</SelectItem>
                          <SelectItem value="Head Office">Head Office</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Phone Number <span className="text-xs text-muted-foreground font-normal">(optional)</span></Label>
                    <Input 
                      placeholder="+27821234567 or 0821234567" 
                      value={newUserForm.phone}
                      onChange={(e) => setNewUserForm(prev => ({ ...prev, phone: e.target.value }))}
                    />
                    <p className="text-xs text-muted-foreground">
                      Use format: +27821234567 (or 0821234567 for South Africa)
                    </p>
                  </div>

                  {/* Commission Rate field - shown for Agent role */}
                  {newUserForm.role === 'Agent' && (
                    <div className="space-y-2">
                      <Label>Commission Rate (%)</Label>
                      <div className="relative">
                        <Input 
                          type="number"
                          step="0.5"
                          min="0"
                          max="100"
                          placeholder="e.g. 3" 
                          value={newUserForm.commissionRate}
                          onChange={(e) => setNewUserForm(prev => ({ ...prev, commissionRate: e.target.value }))}
                          className="pr-8"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Standard commission rate applied to this agent's deals
                      </p>
                    </div>
                  )}

                  {/* Department field - shown for Marketing role */}
                  {newUserForm.role === 'Marketing' && (
                    <div className="space-y-2">
                      <Label>Department</Label>
                      <Input 
                        placeholder="Marketing & Communications" 
                        value={newUserForm.department}
                        onChange={(e) => setNewUserForm(prev => ({ ...prev, department: e.target.value }))}
                      />
                    </div>
                  )}

                  {/* Bio field */}
                  <div className="space-y-2">
                    <Label>Bio <span className="text-xs text-muted-foreground font-normal">(optional)</span></Label>
                    <Textarea 
                      placeholder="Brief bio or description of the user's role and expertise..." 
                      value={newUserForm.bio}
                      onChange={(e) => setNewUserForm(prev => ({ ...prev, bio: e.target.value }))}
                      rows={2}
                    />
                  </div>
                  
                  {/* Password Generation Section */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Password</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-[#FDB022] hover:text-[#FDB022]/80"
                        onClick={generatePassword}
                        data-testid="admin-users-generate-password"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Generate Secure Password
                      </Button>
                    </div>
                    <div className="relative">
                      <Input 
                        type={showGeneratedPassword ? 'text' : 'password'}
                        placeholder="Leave empty to send setup email, or generate one" 
                        value={newUserForm.password}
                        onChange={(e) => {
                          setNewUserForm(prev => ({ ...prev, password: e.target.value }));
                          setPasswordCopied(false);
                        }}
                        className="pr-20"
                      />
                      <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
                        {newUserForm.password && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={copyPasswordToClipboard}
                            data-testid="admin-users-copy-password"
                          >
                            {passwordCopied ? (
                              <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </Button>
                        )}
                        {newUserForm.password && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setShowGeneratedPassword(!showGeneratedPassword)}
                            data-testid="admin-users-toggle-password-visibility"
                          >
                            {showGeneratedPassword ? (
                              <EyeOff className="w-3.5 h-3.5" />
                            ) : (
                              <Eye className="w-3.5 h-3.5" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                    {newUserForm.password && (
                      <div className="space-y-2">
                        <p className="text-xs text-muted-foreground">
                          ⚠️ Make sure to copy and securely share this password with the user
                        </p>
                        <div className="bg-muted/50 p-3 rounded-md border border-border/50">
                          <p className="text-xs font-medium mb-2">Password Requirements:</p>
                          <ul className="text-xs text-muted-foreground space-y-1">
                            <li className={`flex items-center gap-2 ${newUserForm.password.length >= 8 ? 'text-green-600' : ''}`}>
                              {newUserForm.password.length >= 8 ? '✓' : '○'} At least 8 characters
                            </li>
                            <li className={`flex items-center gap-2 ${/[A-Z]/.test(newUserForm.password) ? 'text-green-600' : ''}`}>
                              {/[A-Z]/.test(newUserForm.password) ? '✓' : '○'} One uppercase letter
                            </li>
                            <li className={`flex items-center gap-2 ${/[a-z]/.test(newUserForm.password) ? 'text-green-600' : ''}`}>
                              {/[a-z]/.test(newUserForm.password) ? '✓' : '○'} One lowercase letter
                            </li>
                            <li className={`flex items-center gap-2 ${/[0-9]/.test(newUserForm.password) ? 'text-green-600' : ''}`}>
                              {/[0-9]/.test(newUserForm.password) ? '✓' : '○'} One number
                            </li>
                            <li className={`flex items-center gap-2 ${/[^A-Za-z0-9]/.test(newUserForm.password) ? 'text-green-600' : ''}`}>
                              {/[^A-Za-z0-9]/.test(newUserForm.password) ? '✓' : '○'} One special character (!@#$%^&*)
                            </li>
                          </ul>
                        </div>
                      </div>
                    )}
                    {!newUserForm.password && (
                      <p className="text-xs text-muted-foreground">
                        If no password is set, a password setup email will be sent to the user
                      </p>
                    )}
                  </div>

                  <Button
                    onClick={handleCreateUser}
                    className="w-full"
                    disabled={isCreatingUser}
                    data-testid="admin-users-create-submit"
                  >
                    {isCreatingUser ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating User...
                      </>
                    ) : (
                      'Create User'
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>

          {/* Edit User Dialog */}
          <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit User</DialogTitle>
                <DialogDescription>
                  Update user information and permissions
                </DialogDescription>
              </DialogHeader>
              {editingUser && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <Input 
                        value={editingUser.name}
                        onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input 
                        type="email"
                        value={editingUser.email}
                        onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Role</Label>
                      <Select
                        value={editingUser.role}
                        onValueChange={(value) => setEditingUser({ ...editingUser, role: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="agent">Agent</SelectItem>
                          <SelectItem value="marketing">Marketing</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="external-agent">External Agent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select 
                        value={editingUser.status}
                        onValueChange={(value) => setEditingUser({ ...editingUser, status: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Active">Active</SelectItem>
                          <SelectItem value="Inactive">Inactive</SelectItem>
                          <SelectItem value="Suspended">Suspended</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Region</Label>
                      <Select 
                        value={editingUser.region}
                        onValueChange={(value) => setEditingUser({ ...editingUser, region: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Cape Town">Cape Town</SelectItem>
                          <SelectItem value="Johannesburg">Johannesburg</SelectItem>
                          <SelectItem value="Durban">Durban</SelectItem>
                          <SelectItem value="Pretoria">Pretoria</SelectItem>
                          <SelectItem value="Port Elizabeth">Port Elizabeth</SelectItem>
                          <SelectItem value="Head Office">Head Office</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input 
                        placeholder="+27 82 123 4567"
                        value={editingUser.phone || ''}
                        onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Commission Rate field - shown for Agent role */}
                  {editingUser.role === 'Agent' && (
                    <div className="space-y-2">
                      <Label>Commission Rate (%)</Label>
                      <div className="relative">
                        <Input 
                          type="number"
                          step="0.5"
                          min="0"
                          max="100"
                          placeholder="e.g. 3"
                          value={editingUser.commissionRate ?? ''}
                          onChange={(e) => setEditingUser({ ...editingUser, commissionRate: e.target.value ? parseFloat(e.target.value) : undefined })}
                          className="pr-8"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">%</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Standard commission rate applied to this agent's deals
                      </p>
                    </div>
                  )}

                  {/* Department field - shown for Marketing role */}
                  {editingUser.role === 'Marketing' && (
                    <div className="space-y-2">
                      <Label>Department</Label>
                      <Input 
                        placeholder="Marketing & Communications"
                        value={editingUser.department || ''}
                        onChange={(e) => setEditingUser({ ...editingUser, department: e.target.value })}
                      />
                    </div>
                  )}

                  {/* Bio field */}
                  <div className="space-y-2">
                    <Label>Bio</Label>
                    <Textarea 
                      placeholder="Brief bio or description..."
                      value={editingUser.bio || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, bio: e.target.value })}
                      rows={3}
                    />
                  </div>

                  <div className="flex justify-end space-x-2">
                    <Button variant="outline" onClick={() => setShowEditUserDialog(false)} data-testid="admin-users-edit-cancel">
                      Cancel
                    </Button>
                    <Button onClick={async () => {
                      await handleUpdateUser(editingUser.id, editingUser);
                      setShowEditUserDialog(false);
                      setEditingUser(null);
                    }} data-testid="admin-users-edit-submit">
                      Save Changes
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Role summary badges */}
          <div className="flex flex-wrap gap-2">
            {['All', 'admin', 'agent', 'marketing', 'external-agent'].map((role) => {
              const count = role === 'All' ? users.length : users.filter(u => u.role === role).length;
              if (count === 0 && role !== 'All') return null;
              return (
                <button
                  key={role}
                  onClick={() => { setSearchTerm(role === 'All' ? '' : role); setUserPage(0); }}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    (role === 'All' && !searchTerm) || searchTerm === role
                      ? 'bg-primary text-black'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                  }`}
                  data-testid={`admin-users-filter-${role.toLowerCase()}`}
                >
                  {role === 'All' ? 'All' : getRoleDisplay(role)} ({count})
                </button>
              );
            })}
          </div>

          {/* Search and Filter */}
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder="Search users by name, email, or role..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setUserPage(0); }}
                  className="pl-10"
                />
              </div>
            </div>
            <Button variant="outline" data-testid="admin-users-export">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            {/* Cleanup Old Users button removed — Cognito-only mode */}
          </div>

          {/* Users Table */}
          <Card>
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Groups</TableHead>
                  <TableHead>Last Login</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingAdminData && users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                        <p className="text-muted-foreground">Loading users from Cognito...</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {!isLoadingAdminData && filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12">
                      <div className="flex flex-col items-center gap-3">
                        <Users className="w-8 h-8 text-muted-foreground/50" />
                        <p className="text-muted-foreground">
                          {searchTerm ? `No users matching "${searchTerm}"` : 'No users found. Make sure you are logged in as admin.'}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {filteredUsers.slice(userPage * USERS_PER_PAGE, (userPage + 1) * USERS_PER_PAGE).map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <Avatar className="h-9 w-9">
                          {(user.avatar || user.profilePicture) ? (
                            <AvatarImage src={user.avatar || user.profilePicture} alt={user.name} />
                          ) : null}
                          <AvatarFallback>{(user.name || 'U').split(' ').map((n: string) => n[0]).join('')}</AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{user.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                          {(user.jobTitle || user.bio) && (
                            <p className="text-[11px] text-primary/70 truncate max-w-[220px]" title={user.bio || user.jobTitle}>
                              {user.jobTitle || (user.bio && user.bio.length > 45 ? user.bio.slice(0, 45) + '…' : user.bio)}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        {getRoleIcon(user.role)}
                        <span>{user.role}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getStatusColor(user.status)} flex items-center gap-1.5 w-fit`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          user.status === 'Active' ? 'bg-green-400' : 
                          user.status === 'Inactive' ? 'bg-orange-400' : 'bg-red-400'
                        }`} />
                        {user.status}
                        {user.status === 'Inactive' && (
                          <span className="text-[10px] opacity-70">(Disabled)</span>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {user.cognitoGroups && user.cognitoGroups.length > 0 ? (
                          user.cognitoGroups.map((g: string) => (
                            <Badge key={g} variant="secondary" className="text-[10px] px-1.5 py-0">
                              {g}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-xs text-muted-foreground">{user.region || '—'}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : '—'}</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => {
                          setProfileUser(user);
                          setActiveTab('user-profile');
                        }} title="View profile" data-testid="admin-users-view">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => {
                          setEditingUser(user);
                          setShowEditUserDialog(true);
                        }} title="Edit user" data-testid="admin-users-edit">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setPasswordTargetUser(user);
                            setNewPasswordValue('');
                            setShowNewPassword(false);
                            setShowSetPasswordDialog(true);
                          }}
                          title="Set password"
                          className="hover:text-[#FDB022] hover:bg-[#FDB022]/10"
                          data-testid="admin-users-set-password"
                        >
                          <KeyRound className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setLoginAsTargetUser(user);
                            setLoginAsPassword('');
                            setShowLoginAsPasswordField(false);
                            setShowLoginAsDialog(true);
                          }}
                          title="Login as this user (opens new tab)"
                          className="hover:text-cyan-400 hover:bg-cyan-500/10"
                          data-testid="admin-users-login-as"
                        >
                          <LogIn className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={togglingUserId === user.id}
                          onClick={() => handleToggleUserStatus(user)}
                          title={user.status === 'Active' ? 'Disable user (Cognito)' : 'Enable user (Cognito)'}
                          className={user.status === 'Active'
                            ? 'hover:text-orange-500 hover:bg-orange-500/10'
                            : 'hover:text-green-500 hover:bg-green-500/10'
                          }
                          data-testid="admin-users-toggle-status"
                        >
                          {togglingUserId === user.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : user.status === 'Active' ? (
                            <UserX className="w-4 h-4" />
                          ) : (
                            <UserCheck className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setDeleteTargetUser(user);
                            setDeleteConfirmText('');
                            setShowDeleteDialog(true);
                          }}
                          title="Delete user"
                          className="hover:text-red-500 hover:bg-red-500/10"
                          data-testid="admin-users-delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </Card>

          {/* ───────── User Pagination ───────── */}
          {filteredUsers.length > USERS_PER_PAGE && (
            <div className="flex items-center justify-between px-1">
              <p className="text-sm text-muted-foreground">
                Showing {userPage * USERS_PER_PAGE + 1}–{Math.min((userPage + 1) * USERS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length} users
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={userPage === 0}
                  onClick={() => setUserPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">
                  Page {userPage + 1} of {Math.ceil(filteredUsers.length / USERS_PER_PAGE)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(userPage + 1) * USERS_PER_PAGE >= filteredUsers.length}
                  onClick={() => setUserPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          {/* ───────── Set Password Dialog ───────── */}
          <Dialog open={showSetPasswordDialog} onOpenChange={(open) => {
            setShowSetPasswordDialog(open);
            if (!open) { setPasswordTargetUser(null); setNewPasswordValue(''); }
          }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <KeyRound className="w-5 h-5 text-[#FDB022]" />
                  Set User Password
                </DialogTitle>
                <DialogDescription>
                  Set a new password for <strong>{passwordTargetUser?.name}</strong> ({passwordTargetUser?.email})
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <Alert className="border-amber-500/30 bg-amber-500/5">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  <AlertDescription className="text-xs">
                    This will immediately change the user's Cognito password. Make sure to securely share the new password with the user.
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <div className="relative">
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="Enter new password..."
                      value={newPasswordValue}
                      onChange={(e) => setNewPasswordValue(e.target.value)}
                      className="pr-20"
                    />
                    <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1">
                      {newPasswordValue && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => {
                            navigator.clipboard.writeText(newPasswordValue);
                            toast.success('Password copied to clipboard');
                          }}
                          data-testid="admin-users-copy-new-password"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        data-testid="admin-users-toggle-new-password-visibility"
                      >
                        {showNewPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                      </Button>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-[#FDB022] hover:text-[#FDB022]/80"
                    onClick={() => setNewPasswordValue(generateRandomPassword())}
                    data-testid="admin-users-generate-new-password"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Generate Secure Password
                  </Button>
                </div>
                {newPasswordValue && (
                  <div className="bg-muted/50 p-3 rounded-md border border-border/50">
                    <p className="text-xs font-medium mb-2">Password Strength:</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li className={`flex items-center gap-2 ${newPasswordValue.length >= 8 ? 'text-green-600' : ''}`}>
                        {newPasswordValue.length >= 8 ? '✓' : '○'} At least 8 characters
                      </li>
                      <li className={`flex items-center gap-2 ${/[A-Z]/.test(newPasswordValue) ? 'text-green-600' : ''}`}>
                        {/[A-Z]/.test(newPasswordValue) ? '✓' : '○'} One uppercase letter
                      </li>
                      <li className={`flex items-center gap-2 ${/[a-z]/.test(newPasswordValue) ? 'text-green-600' : ''}`}>
                        {/[a-z]/.test(newPasswordValue) ? '✓' : '○'} One lowercase letter
                      </li>
                      <li className={`flex items-center gap-2 ${/[0-9]/.test(newPasswordValue) ? 'text-green-600' : ''}`}>
                        {/[0-9]/.test(newPasswordValue) ? '✓' : '○'} One number
                      </li>
                      <li className={`flex items-center gap-2 ${/[^A-Za-z0-9]/.test(newPasswordValue) ? 'text-green-600' : ''}`}>
                        {/[^A-Za-z0-9]/.test(newPasswordValue) ? '✓' : '○'} One special character
                      </li>
                    </ul>
                  </div>
                )}
                <div className="flex justify-end space-x-2 pt-2">
                  <Button variant="outline" onClick={() => setShowSetPasswordDialog(false)} data-testid="admin-users-set-password-cancel">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSetPassword}
                    disabled={isSettingPassword || !newPasswordValue || newPasswordValue.length < 8}
                    className="bg-[#FDB022] text-black hover:bg-[#FDB022]/90"
                    data-testid="admin-users-set-password-submit"
                  >
                    {isSettingPassword ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting Password...</>
                    ) : (
                      <><KeyRound className="w-4 h-4 mr-2" /> Set Password</>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* ───────── Login As User Dialog ───────── */}
          <Dialog open={showLoginAsDialog} onOpenChange={(open) => {
            setShowLoginAsDialog(open);
            if (!open) { setLoginAsTargetUser(null); setLoginAsPassword(''); }
          }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <LogIn className="w-5 h-5 text-cyan-400" />
                  Login As User
                </DialogTitle>
                <DialogDescription>
                  Sign in as <strong>{loginAsTargetUser?.name}</strong> ({loginAsTargetUser?.email}) — a new browser tab will open with their session.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <Alert className="border-cyan-500/30 bg-cyan-500/5">
                  <AlertCircle className="h-4 w-4 text-cyan-400" />
                  <AlertDescription className="text-xs">
                    You need to provide the user's current password to login as them. If you don't know it, use "Set Password" first to reset it, then come back here.
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  <Label>User's Password</Label>
                  <div className="relative">
                    <Input
                      type={showLoginAsPasswordField ? 'text' : 'password'}
                      placeholder="Enter the user's password..."
                      value={loginAsPassword}
                      onChange={(e) => setLoginAsPassword(e.target.value)}
                      className="pr-10"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && loginAsPassword) handleLoginAsUser();
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                      onClick={() => setShowLoginAsPasswordField(!showLoginAsPasswordField)}
                      data-testid="admin-users-toggle-login-as-password-visibility"
                    >
                      {showLoginAsPasswordField ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
                <div className="flex justify-end space-x-2 pt-2">
                  <Button variant="outline" onClick={() => setShowLoginAsDialog(false)} data-testid="admin-users-login-as-cancel">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleLoginAsUser}
                    disabled={isLoggingInAs || !loginAsPassword}
                    className="bg-cyan-600 text-white hover:bg-cyan-700"
                    data-testid="admin-users-login-as-submit"
                  >
                    {isLoggingInAs ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Logging in...</>
                    ) : (
                      <><ExternalLink className="w-4 h-4 mr-2" /> Open as {loginAsTargetUser?.name?.split(' ')[0]}</>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* ───────── Delete User Confirmation Dialog ───────── */}
          <Dialog open={showDeleteDialog} onOpenChange={(open) => {
            setShowDeleteDialog(open);
            if (!open) { setDeleteTargetUser(null); setDeleteConfirmText(''); }
          }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-400">
                  <AlertTriangle className="w-5 h-5" />
                  Delete User
                </DialogTitle>
                <DialogDescription>
                  This will permanently remove <strong>{deleteTargetUser?.name}</strong> ({deleteTargetUser?.email}) from the system and disable their Cognito account.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <Alert className="border-red-500/30 bg-red-500/5">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  <AlertDescription className="text-xs text-red-300">
                    <strong>This action cannot be undone.</strong> The user's KV profile data will be deleted and their Cognito account will be disabled. They will no longer be able to log in.
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  <Label className="text-sm">
                    Type <span className="font-mono text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">DELETE</span> to confirm:
                  </Label>
                  <Input
                    placeholder="Type DELETE..."
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    className="border-red-500/30 focus-visible:ring-red-500/50"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && deleteConfirmText === 'DELETE') handleDeleteUserConfirmed();
                    }}
                  />
                </div>
                <div className="flex justify-end space-x-2 pt-2">
                  <Button variant="outline" onClick={() => setShowDeleteDialog(false)} data-testid="admin-users-delete-cancel">
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={handleDeleteUserConfirmed}
                    disabled={isDeletingUser || deleteConfirmText !== 'DELETE'}
                    data-testid="admin-users-delete-confirm"
                  >
                    {isDeletingUser ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>
                    ) : (
                      <><Trash2 className="w-4 h-4 mr-2" /> Delete User</>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* F-022: Agents tab — dedicated agent management view */}
        <TabsContent value="agents" className="space-y-6" data-testid="admin-agents-tab">
          <div className="flex items-center justify-between">
            <div>
              <h3>Agent Management</h3>
              <p className="text-muted-foreground">
                Manage registered agents · <span className="text-primary">{users.filter(u => u.role === 'Agent' || (u.cognitoGroups || []).includes('agent')).length} agents</span>
              </p>
            </div>
            <Button onClick={() => setActiveTab('users')} data-testid="admin-agents-manage-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Agent
            </Button>
          </div>
          <Card>
            <div className="overflow-x-auto">
              <Table data-testid="admin-agents-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Region</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.filter(u => u.role === 'Agent' || (u.cognitoGroups || []).includes('agent')).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No agents found. Use User Management to create agent accounts.
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.filter(u => u.role === 'Agent' || (u.cognitoGroups || []).includes('agent')).map(agent => (
                      <TableRow key={agent.id}>
                        <TableCell className="font-medium">{agent.name}</TableCell>
                        <TableCell>{agent.email}</TableCell>
                        <TableCell>
                          <Badge variant={agent.status === 'Active' || agent.status === 'CONFIRMED' ? 'default' : 'secondary'}>
                            {agent.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{agent.region || '—'}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => setActiveTab('users')}>
                            Manage
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="sellers" className="space-y-6">
          <div>
            <h3>Seller Management</h3>
            <p className="text-muted-foreground">Manage property sellers and their listings</p>
          </div>

          {/* Seller Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Sellers</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{sellers.length}</div>
                <p className="text-xs text-muted-foreground">
                  {sellers.filter(s => s.status === 'active').length} active
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Listings</CardTitle>
                <Home className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{sellerProperties.length}</div>
                <p className="text-xs text-muted-foreground">
                  {sellerProperties.filter(p => p.status === 'pending').length} pending review
                </p>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => setShowApprovedPropertiesDialog(true)}
            >
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Approved Properties</CardTitle>
                <CheckCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {sellerProperties.filter(p => p.status === 'approved').length}
                </div>
                <p className="text-xs text-muted-foreground">
                  Click to view all • Live on platform
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Sellers Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Registered Sellers ({sellers.length})</CardTitle>
                <Input
                  placeholder="Search by name or email..."
                  value={sellerSearchTerm}
                  onChange={(e) => setSellerSearchTerm(e.target.value)}
                  className="w-64"
                />
              </div>
            </CardHeader>
            <CardContent>
              {sellers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Sellers Yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Click "Add Joe Brown + 3 Properties" below to seed test data
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Seller</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Properties</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Registered</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sellers
                      .filter(seller => {
                        if (!sellerSearchTerm) return true;
                        const name = (seller.name || [seller.firstName, seller.lastName].filter(Boolean).join(' ')).toLowerCase();
                        const email = (seller.email || '').toLowerCase();
                        const term = sellerSearchTerm.toLowerCase();
                        return name.includes(term) || email.includes(term);
                      })
                      .map((seller) => {
                      const sellerName = seller.name || [seller.firstName, seller.lastName].filter(Boolean).join(' ') || 'Unknown Seller';
                      const sid = seller.sellerId || seller.id;
                      const listedCount = sellerProperties.filter(p => p.sellerId === sid).length;
                      const approvedCount = sellerProperties.filter(p => p.sellerId === sid && p.status === 'approved').length;
                      return (
                      <TableRow key={sid}>
                        <TableCell>
                          <div
                            className="flex items-center space-x-3 cursor-pointer hover:opacity-80"
                            onClick={() => setSelectedSeller(seller)}
                          >
                            <Avatar>
                              <AvatarFallback>{sellerName.split(' ').map((n: string) => n[0]).join('')}</AvatarFallback>
                            </Avatar>
                            <span className="font-medium">{sellerName}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>{seller.email}</p>
                            <p className="text-muted-foreground">{seller.phone}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>{listedCount} listed</p>
                            <p className="text-muted-foreground">{approvedCount} approved</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            seller.status === 'active' ? 'bg-green-100 text-green-800' :
                            seller.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {seller.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{seller.registeredDate || seller.createdAt ? new Date(seller.registeredDate || seller.createdAt).toLocaleDateString() : 'N/A'}</p>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Button variant="ghost" size="sm" onClick={() => setSelectedSeller(seller)} data-testid="admin-sellers-view">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => {
                              setEditingSeller(seller);
                              setShowEditSellerDialog(true);
                            }} data-testid="admin-sellers-edit">
                              <Edit className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );})}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Seller Properties Pending Review */}
          <Card>
            <CardHeader>
              <CardTitle>Seller Properties Pending Review</CardTitle>
            </CardHeader>
            <CardContent>
              {sellerProperties.filter(p => p.status === 'pending').length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Pending Properties</h3>
                  <p className="text-muted-foreground">
                    All seller properties have been reviewed
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {sellerProperties
                    .filter(p => p.status === 'pending')
                    .map((property) => {
                    const seller = sellers.find(s => s.sellerId === property.sellerId || s.id === property.sellerId);
                    return (
                      <div key={property.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-medium">{property.title}</h4>
                              <Badge variant="outline">
                                <Clock className="w-3 h-3 mr-1" />
                                Pending
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              {property.city || property.location?.city || '—'} • R{(property.price || 0).toLocaleString()}
                            </p>
                            <p className="text-xs text-muted-foreground mb-2">
                              {property.bedrooms} bed • {property.bathrooms} bath • {property.size} m² • {property.propertyType}
                            </p>
                            <p className="text-sm mb-2 line-clamp-2">{property.description}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>Listed by: {seller ? (seller.name || [seller.firstName, seller.lastName].filter(Boolean).join(' ') || property.sellerId) : property.sellerId}</span>
                              <span>•</span>
                              <span>{property.createdAt ? new Date(property.createdAt).toLocaleDateString() : property.createdDate ? new Date(property.createdDate).toLocaleDateString() : '—'}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Button
                              size="sm"
                              onClick={() => {
                                if (window.confirm('Approve this property listing?')) {
                                  handleApproveProperty(property.id);
                                }
                              }}
                              data-testid="admin-sellers-property-approve"
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRejectProperty(property.id)}
                              data-testid="admin-sellers-property-reject"
                            >
                              <XCircle className="w-4 h-4 mr-2" />
                              Reject
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setViewingProperty(property)} data-testid="admin-sellers-property-view">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* All Approved Properties */}
          <Card>
            <CardHeader>
              <CardTitle>All Approved Properties ({sellerProperties.filter(p => p.status === 'approved').length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {sellerProperties
                  .filter(p => p.status === 'approved')
                  .map((property) => {
                    const seller = sellers.find(s => s.sellerId === property.sellerId || s.id === property.sellerId);
                    return (
                      <div key={property.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-medium">{property.title}</h4>
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Approved
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              {property.city || property.location?.city || '—'} • R{(property.price || 0).toLocaleString()}
                            </p>
                            <p className="text-xs text-muted-foreground mb-2">
                              {property.bedrooms} bed • {property.bathrooms} bath • {property.size} m² • {property.propertyType}
                            </p>
                            <p className="text-sm mb-2 line-clamp-2">{property.description}</p>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>Listed by: {seller ? (seller.name || [seller.firstName, seller.lastName].filter(Boolean).join(' ') || property.sellerId) : property.sellerId}</span>
                              <span>•</span>
                              <span>Approved: {property.approvedDate ? new Date(property.approvedDate).toLocaleDateString() : 'N/A'}</span>
                              {property.leadCount && (
                                <>
                                  <span>•</span>
                                  <span>{property.leadCount} leads</span>
                                </>
                              )}
                              {property.viewings && property.viewings.length > 0 && (
                                <>
                                  <span>•</span>
                                  <span>{property.viewings.length} viewings</span>
                                </>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <Button size="sm" variant="ghost" onClick={() => {
                              toast.info('Property detail view coming soon');
                            }} data-testid="admin-sellers-approved-property-view">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                {sellerProperties.filter(p => p.status === 'approved').length === 0 && (
                  <div className="text-center py-12">
                    <CheckCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Approved Properties</h3>
                    <p className="text-muted-foreground">
                      Approve pending seller properties to see them here
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="listings" className="space-y-6">
          <PropertyListingManager properties={allProperties} onPropertiesChanged={loadAllProperties} />
        </TabsContent>

        {/* Transfer Signings Tab */}
        <TabsContent value="signings">
          <TransferSignings />
        </TabsContent>

        {/* Email Management Tab */}
        <TabsContent value="email-management">
          <AdminEmailManagement users={users} />
        </TabsContent>

        <TabsContent value="whatsapp" className="space-y-6">
          <WhatsAppConfig />
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          <div>
            <h3>Platform Settings</h3>
            <p className="text-muted-foreground">Configure platform behavior and features</p>
          </div>

          {/* Economic Indicators & Calculator Defaults */}
          <EconomicIndicators />

          {/* Brand Settings */}
          <BrandSettings />

          {/* Contact and Social Settings */}
          <ContactSocialSettings />

          {/* Lead Management Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5 text-[#FDB022]" />
                Lead Management
              </CardTitle>
              <CardDescription>Control how new leads are distributed to agents</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border border-white/10 bg-white/5">
                <div>
                  <p className="font-medium">Round-Robin Lead Allocation</p>
                  <p className="text-sm text-muted-foreground">
                    Automatically distribute new leads evenly across all active agents in sequence.
                    When disabled, admins must manually assign leads.
                  </p>
                </div>
                <Switch
                  checked={platformSettings.find(s => s.key === 'round-robin-enabled')?.value === true}
                  onCheckedChange={(checked) => handleUpdateSetting('round-robin-enabled', checked)}
                  data-testid="round-robin-toggle"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3>System Alerts &amp; Health</h3>
              <p className="text-muted-foreground">Live AWS infrastructure health, system status, costs &amp; notifications</p>
            </div>
            <div className="flex items-center gap-2">
              {lastHealthRefresh && (
                <span className="text-xs text-muted-foreground">
                  Last refreshed: {lastHealthRefresh.toLocaleTimeString()}
                </span>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={loadAwsSystemData}
                disabled={isLoadingHealth || isLoadingStatus || isLoadingCost}
              >
                {(isLoadingHealth || isLoadingStatus || isLoadingCost) ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-1" />
                )}
                Refresh All
              </Button>
            </div>
          </div>

          {/* ── AWS Health Check Dashboard ── */}
          <Card className="border-zinc-800 bg-zinc-900/50">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Server className="w-5 h-5 text-[#FDB022]" />
                  AWS Infrastructure Health
                </CardTitle>
                {awsHealth && (
                  <Badge className={
                    awsHealth.status === 'healthy' ? 'bg-green-500/20 text-green-400 border-green-500/30' :
                    awsHealth.status === 'degraded' ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' :
                    'bg-red-500/20 text-red-400 border-red-500/30'
                  }>
                    {awsHealth.status === 'healthy' ? <CheckCircle className="w-3 h-3 mr-1" /> :
                     awsHealth.status === 'degraded' ? <AlertTriangle className="w-3 h-3 mr-1" /> :
                     <XCircle className="w-3 h-3 mr-1" />}
                    {awsHealth.status?.toUpperCase()}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingHealth ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#FDB022]" />
                  <span className="ml-2 text-muted-foreground">Loading health data...</span>
                </div>
              ) : healthError ? (
                <Alert className="border-red-500/50">
                  <XCircle className="w-4 h-4 text-red-500" />
                  <AlertTitle>Health Check Failed</AlertTitle>
                  <AlertDescription>{healthError}</AlertDescription>
                </Alert>
              ) : awsHealth ? (
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-zinc-800/60 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-white">{awsHealth.summary?.total}</div>
                      <div className="text-xs text-muted-foreground">Total Services</div>
                    </div>
                    <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-green-400">{awsHealth.summary?.healthy}</div>
                      <div className="text-xs text-green-400/70">Healthy</div>
                    </div>
                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-yellow-400">{awsHealth.summary?.degraded}</div>
                      <div className="text-xs text-yellow-400/70">Degraded</div>
                    </div>
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-red-400">{awsHealth.summary?.unhealthy}</div>
                      <div className="text-xs text-red-400/70">Unhealthy</div>
                    </div>
                  </div>

                  {/* Individual Service Checks */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-medium text-muted-foreground">Service Details</h4>
                    <div className="grid gap-2">
                      {(awsHealth.checks ?? []).map((check: HealthCheckResult, idx: number) => (
                        <div key={idx} className="flex items-center justify-between bg-zinc-800/40 rounded-lg px-4 py-3 border border-zinc-700/50">
                          <div className="flex items-center gap-3">
                            {check.status === 'healthy' ? (
                              <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                            ) : check.status === 'degraded' ? (
                              <AlertTriangle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                            )}
                            <div>
                              <div className="text-sm font-medium text-white">{check.service}</div>
                              <div className="text-xs text-muted-foreground">{check.message}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {check.responseTime !== undefined && (
                              <span className="text-xs text-muted-foreground">
                                <Zap className="w-3 h-3 inline mr-1" />
                                {check.responseTime}ms
                              </span>
                            )}
                            {check.details && (check.details as any).itemCount !== undefined && (
                              <span className="text-xs text-muted-foreground">
                                <Database className="w-3 h-3 inline mr-1" />
                                {(check.details as any).itemCount} items
                              </span>
                            )}
                            <Badge variant="outline" className={
                              check.status === 'healthy' ? 'border-green-500/50 text-green-400' :
                              check.status === 'degraded' ? 'border-yellow-500/50 text-yellow-400' :
                              'border-red-500/50 text-red-400'
                            }>
                              {check.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="text-xs text-muted-foreground text-right">
                    Checked at: {awsHealth.timestamp ? new Date(awsHealth.timestamp).toLocaleString() : 'Unknown'}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <Server className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No health data available. Click "Refresh All" to load.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── System Status + Cost Report side by side ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="border-zinc-800 bg-zinc-900/50">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Activity className="w-5 h-5 text-[#FDB022]" />
                  System Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingStatus ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin text-[#FDB022]" />
                  </div>
                ) : statusError ? (
                  <div className="text-sm text-red-400">{statusError}</div>
                ) : awsStatus ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-muted-foreground">Environment:</span>
                      <Badge variant="outline">{awsStatus.environment}</Badge>
                      <span className="text-sm text-muted-foreground ml-2">Region:</span>
                      <Badge variant="outline">{awsStatus.region}</Badge>
                    </div>

                    {awsStatus.metrics?.lambda && (
                      <div className="bg-zinc-800/40 rounded-lg p-3 border border-zinc-700/50">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                          <Cpu className="w-4 h-4" />
                          Lambda Invocations
                        </div>
                        <div className="text-xl font-bold text-white">
                          {awsStatus.metrics.lambda.invocations.toLocaleString()}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Period: {awsStatus.metrics.lambda.period}
                        </div>
                      </div>
                    )}

                    {awsStatus.syncStatus && awsStatus.syncStatus.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-muted-foreground">Data Sync Status</h4>
                        {awsStatus.syncStatus.map((sync, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-zinc-800/40 rounded-lg px-3 py-2 border border-zinc-700/50">
                            <div className="flex items-center gap-2">
                              <Wifi className="w-4 h-4 text-blue-400" />
                              <span className="text-sm text-white capitalize">{sync.source}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {sync.count !== undefined && (
                                <span className="text-xs text-muted-foreground">{sync.count} items</span>
                              )}
                              <Badge variant="outline" className={
                                sync.lastSyncStatus === 'success' ? 'border-green-500/50 text-green-400' :
                                sync.lastSyncStatus === 'error' ? 'border-red-500/50 text-red-400' :
                                'border-zinc-500/50 text-zinc-400'
                              }>
                                {sync.lastSyncStatus}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground text-sm">No status data available</div>
                )}
              </CardContent>
            </Card>

            <Card className="border-zinc-800 bg-zinc-900/50">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <CircleDollarSign className="w-5 h-5 text-[#FDB022]" />
                    AWS Cost Report
                  </CardTitle>
                  <Select value={costPeriod} onValueChange={handleCostPeriodChange}>
                    <SelectTrigger className="w-[160px] h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="current_month">Current Month</SelectItem>
                      <SelectItem value="last_month">Last Month</SelectItem>
                      <SelectItem value="last_7_days">Last 7 Days</SelectItem>
                      <SelectItem value="last_30_days">Last 30 Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {isLoadingCost ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin text-[#FDB022]" />
                  </div>
                ) : costError ? (
                  <div className="text-sm text-red-400">{costError}</div>
                ) : awsCostReport ? (
                  <div className="space-y-4">
                    <div className="bg-zinc-800/40 rounded-lg p-4 border border-zinc-700/50 text-center">
                      <div className="text-3xl font-bold text-white">
                        {awsCostReport.currency === 'USD' ? '$' : awsCostReport.currency}{(Number(awsCostReport.totalCost) || 0).toFixed(2)}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {awsCostReport.startDate} &mdash; {awsCostReport.endDate}
                      </div>
                    </div>

                    {awsCostReport.serviceBreakdown && awsCostReport.serviceBreakdown.length > 0 && (
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-muted-foreground">Service Breakdown</h4>
                        {awsCostReport.serviceBreakdown.map((svc: CostServiceBreakdown, idx: number) => (
                          <div key={idx} className="space-y-1">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-white truncate max-w-[180px]">{svc.service}</span>
                              <span className="text-muted-foreground font-mono">
                                ${(Number(svc.cost) || 0).toFixed(2)} ({(Number(svc.percentage) || 0).toFixed(1)}%)
                              </span>
                            </div>
                            <div className="w-full bg-zinc-800 rounded-full h-1.5">
                              <div
                                className="bg-[#FDB022] rounded-full h-1.5 transition-all duration-300"
                                style={{ width: `${Math.min(svc.percentage, 100)}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground text-sm">No cost data available</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* ── Existing Platform Alerts ── */}
          <Separator className="bg-zinc-700" />
          <div>
            <h4 className="text-sm font-medium mb-3">Platform Alerts</h4>
          </div>
          <div className="space-y-4">
            {systemAlerts.length > 0 ? systemAlerts.map((alert) => (
              <Alert key={alert.id} className={
                alert.severity === 'Critical' ? 'border-red-500' :
                alert.severity === 'High' ? 'border-orange-500' :
                alert.severity === 'Medium' ? 'border-yellow-500' : 'border-blue-500'
              }>
                <div className="flex items-center space-x-2">
                  {getAlertIcon(alert.type)}
                  <AlertTitle className="flex items-center space-x-2">
                    <span>{alert.title}</span>
                    <Badge variant="outline">{alert.severity}</Badge>
                    {!alert.acknowledged && (
                      <Badge variant="destructive" className="text-xs">Unacknowledged</Badge>
                    )}
                  </AlertTitle>
                </div>
                <AlertDescription className="mt-2">
                  <p>{alert.message}</p>
                  <div className="flex items-center justify-between mt-3">
                    <div className="text-xs text-muted-foreground">
                      {new Date(alert.timestamp).toLocaleString()} • {alert.source}
                    </div>
                    {!alert.acknowledged && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAcknowledgeAlert(alert.id)}
                        data-testid="admin-alerts-acknowledge"
                      >
                        Acknowledge
                      </Button>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )) : (
              <div className="text-center py-6 text-muted-foreground text-sm">
                <Bell className="w-6 h-6 mx-auto mb-2 opacity-30" />
                No platform alerts at this time.
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="logs" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3>Audit Logs</h3>
              <p className="text-muted-foreground">Track all system actions and user activities</p>
            </div>
            <div className="relative w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search logs..."
                value={auditSearchTerm}
                onChange={(e) => {
                  setAuditSearchTerm(e.target.value);
                  setAuditCurrentPage(1); // Reset to first page on search
                }}
                className="pl-9"
              />
            </div>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>IP Address</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedAuditLogs.length > 0 ? (
                  paginatedAuditLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-sm">
                        {new Date(log.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p className="font-medium">{log.userName}</p>
                          <p className="text-muted-foreground text-xs">{log.userId}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{log.action}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{log.resource}</TableCell>
                      <TableCell className="text-sm max-w-xs truncate">{log.details}</TableCell>
                      <TableCell className="text-sm">{log.ipAddress}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            
            {/* Pagination */}
            {totalAuditPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t">
                <div className="text-sm text-muted-foreground">
                  Showing {((auditCurrentPage - 1) * auditLogsPerPage) + 1} to {Math.min(auditCurrentPage * auditLogsPerPage, filteredAuditLogs.length)} of {filteredAuditLogs.length} logs
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAuditCurrentPage(Math.max(1, auditCurrentPage - 1))}
                    disabled={auditCurrentPage === 1}
                    data-testid="admin-audit-logs-previous"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalAuditPages }, (_, i) => i + 1).map((page) => (
                      <Button
                        key={page}
                        variant={page === auditCurrentPage ? "default" : "outline"}
                        size="sm"
                        onClick={() => setAuditCurrentPage(page)}
                        className="w-8 h-8 p-0"
                        data-testid={`admin-audit-logs-page-${page}`}
                      >
                        {page}
                      </Button>
                    ))}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAuditCurrentPage(Math.min(totalAuditPages, auditCurrentPage + 1))}
                    disabled={auditCurrentPage === totalAuditPages}
                    data-testid="admin-audit-logs-next"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="stock-sharing">
          <div className="mb-6">
            <h3>Agent Stock Sharing Feed</h3>
            <p className="text-muted-foreground">Monitor and manage stock sharing activity between internal and external agents</p>
          </div>
          <StockSharingFeed 
            source="admin"
            availableProperties={properties
              .filter(p => p.status === 'Published')
              .map(p => ({ id: p.id, title: p.title }))}
            onViewProperty={(propertyId) => {
              // Admin can view properties too
              const property = properties.find(p => p.id === propertyId);
              if (property) {
                window.alert(`Property: ${property.title} - This would navigate to property detail page`);
              }
            }}
          />
        </TabsContent>

        {/* User Profile Tab */}
        <TabsContent value="user-profile">
          {profileUser ? (
            <UserProfileTab
              user={profileUser}
              onBack={() => {
                setProfileUser(null);
                setActiveTab('users');
              }}
            />
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>No user selected. Go back to User Management to select a user.</p>
              <Button variant="outline" className="mt-4" onClick={() => setActiveTab('users')}>
                Back to User Management
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="agent-sync" className="space-y-6">
          <AdminAgentSync />
        </TabsContent>

        <TabsContent value="knowledge-hub" className="space-y-6">
          <AdminKnowledgeHub />
        </TabsContent>
        <TabsContent value="referrals" className="space-y-6">
          <AdminReferrals />
        </TabsContent>
      </Tabs>

      {/* Seller Dialogs - Outside tabs for global accessibility */}
      {/* View Seller Dialog */}
      <Dialog open={!!selectedSeller} onOpenChange={() => setSelectedSeller(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Seller Details</DialogTitle>
            <DialogDescription>
              View seller information and activity
            </DialogDescription>
          </DialogHeader>
          {selectedSeller && (() => {
            const sellerDisplayName = selectedSeller.name || [selectedSeller.firstName, selectedSeller.lastName].filter(Boolean).join(' ') || 'Unknown Seller';
            return (
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <Avatar className="w-16 h-16">
                  <AvatarFallback className="text-xl">
                    {sellerDisplayName.split(' ').map((n: string) => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-semibold">{sellerDisplayName}</h3>
                  <p className="text-sm text-muted-foreground">{selectedSeller.email}</p>
                </div>
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Phone</Label>
                  <p className="font-medium">{selectedSeller.phone}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <Badge className={
                    selectedSeller.status === 'active' ? 'bg-green-100 text-green-800' :
                    selectedSeller.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }>
                    {selectedSeller.status}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">Properties Listed</Label>
                  <p className="font-medium">{selectedSeller.propertiesListed}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Properties Approved</Label>
                  <p className="font-medium">{selectedSeller.propertiesApproved}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Registered Date</Label>
                  <p className="font-medium">{
                    selectedSeller.registeredDate
                      ? new Date(selectedSeller.registeredDate).toLocaleDateString()
                      : selectedSeller.createdAt
                        ? new Date(selectedSeller.createdAt).toLocaleDateString()
                        : '—'
                  }</p>
                </div>
              </div>

              <Separator />

              {/* Seller's Properties */}
              <div>
                <h4 className="font-semibold mb-3">Properties</h4>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {sellerProperties
                    .filter(p => p.sellerId === (selectedSeller.sellerId || selectedSeller.id))
                    .map((property) => (
                      <div key={property.id} className="border rounded-lg p-3">
                        <div className="flex items-start justify-between mb-2">
                          <h5 className="font-medium text-sm">{property.title}</h5>
                          <Badge className={
                            property.status === 'approved' ? 'bg-green-100 text-green-800' :
                            property.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }>
                            {property.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mb-1">
                          {property.city || property.location?.city || '—'}
                        </p>
                        <p className="text-sm font-semibold mb-1">
                          R{(property.price || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {property.bedrooms} bed • {property.bathrooms} bath • {property.size} m²
                        </p>
                        {property.leadCount !== undefined && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {property.leadCount} leads
                            {property.viewings && property.viewings.length > 0 &&
                              ` • ${property.viewings.length} viewings`
                            }
                          </p>
                        )}
                      </div>
                    ))}
                  {sellerProperties.filter(p => p.sellerId === (selectedSeller.sellerId || selectedSeller.id)).length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No properties listed yet
                    </p>
                  )}
                </div>
              </div>
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Edit Seller Dialog */}
      <Dialog open={showEditSellerDialog} onOpenChange={setShowEditSellerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Seller</DialogTitle>
            <DialogDescription>
              Update seller information and status
            </DialogDescription>
          </DialogHeader>
          {editingSeller && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name</Label>
                  <Input
                    value={editingSeller.firstName || editingSeller.name?.split(' ')[0] || ''}
                    onChange={(e) => setEditingSeller({ ...editingSeller, firstName: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input
                    value={editingSeller.lastName || editingSeller.name?.split(' ').slice(1).join(' ') || ''}
                    onChange={(e) => setEditingSeller({ ...editingSeller, lastName: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input 
                    type="email"
                    value={editingSeller.email}
                    onChange={(e) => setEditingSeller({ ...editingSeller, email: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input 
                    value={editingSeller.phone}
                    onChange={(e) => setEditingSeller({ ...editingSeller, phone: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select 
                    value={editingSeller.status}
                    onValueChange={(value) => setEditingSeller({ ...editingSeller, status: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="suspended">Suspended</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowEditSellerDialog(false)} data-testid="admin-sellers-edit-cancel">
                  Cancel
                </Button>
                <Button onClick={async () => {
                  try {
                    const sellerId = editingSeller.sellerId || editingSeller.id;
                    // Call API to update seller
                    await sellerAPI.updateSeller(sellerId, {
                      firstName: editingSeller.firstName,
                      lastName: editingSeller.lastName,
                      email: editingSeller.email,
                      phone: editingSeller.phone,
                      status: editingSeller.status
                    });

                    // Update seller in state
                    setSellers(prev => prev.map(s =>
                      (s.sellerId || s.id) === sellerId ? editingSeller : s
                    ));

                    toast.success('Seller information updated');
                    setShowEditSellerDialog(false);
                    setEditingSeller(null);
                    
                    // Log audit
                    await adminAuditAPI.createAuditLog({
                      user: 'Admin',
                      action: 'Updated seller',
                      resource: `Seller: ${editingSeller.name || [editingSeller.firstName, editingSeller.lastName].filter(Boolean).join(' ')}`,
                      details: `Modified seller account information`,
                      ipAddress: 'N/A',
                    }).catch(err => logger.error('Failed to log audit', err as Error, undefined, 'AdminHub'));
                  } catch (error) {
                    logger.error('Error updating seller', error as Error, undefined, 'AdminHub');
                    toast.error('Failed to update seller');
                  }
                }} data-testid="admin-sellers-edit-submit">
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Approved Properties Dialog */}
      <Dialog open={showApprovedPropertiesDialog} onOpenChange={setShowApprovedPropertiesDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>All Approved Properties</DialogTitle>
            <DialogDescription>
              Complete list of all approved properties on the platform
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {sellerProperties
              .filter(p => p.status === 'approved')
              .map((property) => {
                const seller = sellers.find(s => s.sellerId === property.sellerId || s.id === property.sellerId);
                return (
                  <div key={property.id} className="border rounded-lg p-4 hover:bg-accent/50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium">{property.title}</h4>
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Approved
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {property.location?.address}, {property.location?.city} • R{(property.price || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground mb-2">
                          {property.bedrooms} bed • {property.bathrooms} bath �� {property.size} m² • {property.propertyType}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>Listed by: {seller ? (seller.name || [seller.firstName, seller.lastName].filter(Boolean).join(' ') || 'Unknown') : 'Unknown'}</span>
                          <span>•</span>
                          <span>Approved: {property.approvedDate ? new Date(property.approvedDate).toLocaleDateString() : 'N/A'}</span>
                          {property.leadCount !== undefined && (
                            <>
                              <span>•</span>
                              <span>{property.leadCount} leads</span>
                            </>
                          )}
                          {property.viewings && property.viewings.length > 0 && (
                            <>
                              <span>•</span>
                              <span>{property.viewings.length} viewings</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedSeller(seller);
                            setShowApprovedPropertiesDialog(false);
                          }}
                          data-testid="admin-sellers-approved-property-view-seller"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Seller
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            {sellerProperties.filter(p => p.status === 'approved').length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No approved properties yet</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Seller Property Detail Modal */}
      <Dialog open={!!viewingProperty} onOpenChange={() => setViewingProperty(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewingProperty?.title || 'Property Detail'}</DialogTitle>
            <DialogDescription>
              Full submission details — review before approving or rejecting
            </DialogDescription>
          </DialogHeader>
          {viewingProperty && (() => {
            const seller = sellers.find(s => s.sellerId === viewingProperty.sellerId || s.id === viewingProperty.sellerId);
            const sellerName = seller
              ? ([seller.firstName, seller.lastName].filter(Boolean).join(' ') || seller.name || viewingProperty.sellerId)
              : viewingProperty.sellerId;
            const images: string[] = Array.isArray(viewingProperty.images) ? viewingProperty.images : [];
            const features: string[] = Array.isArray(viewingProperty.features) ? viewingProperty.features : [];
            return (
              <div className="space-y-5">
                {/* Images */}
                {images.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {images.slice(0, 4).map((img, i) => (
                      <img
                        key={i}
                        src={img}
                        alt={`Property image ${i + 1}`}
                        className="w-full h-40 object-cover rounded-lg border"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    ))}
                  </div>
                )}

                {/* Status + Seller */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant={viewingProperty.status === 'pending' ? 'outline' : viewingProperty.status === 'approved' ? 'default' : 'destructive'}>
                      {viewingProperty.status}
                    </Badge>
                    {viewingProperty.propertyType && <Badge variant="secondary">{viewingProperty.propertyType}</Badge>}
                    {viewingProperty.listingType && <Badge variant="secondary">{viewingProperty.listingType}</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground">Submitted by <span className="font-medium text-foreground">{sellerName}</span></p>
                </div>

                {/* Price */}
                <div>
                  <p className="text-2xl font-bold">R{(viewingProperty.price || 0).toLocaleString()}</p>
                </div>

                {/* Key stats */}
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div className="border rounded-lg p-3">
                    <p className="text-lg font-semibold">{viewingProperty.bedrooms ?? '—'}</p>
                    <p className="text-xs text-muted-foreground">Bedrooms</p>
                  </div>
                  <div className="border rounded-lg p-3">
                    <p className="text-lg font-semibold">{viewingProperty.bathrooms ?? '—'}</p>
                    <p className="text-xs text-muted-foreground">Bathrooms</p>
                  </div>
                  <div className="border rounded-lg p-3">
                    <p className="text-lg font-semibold">{viewingProperty.parkingSpaces ?? '—'}</p>
                    <p className="text-xs text-muted-foreground">Parking</p>
                  </div>
                  <div className="border rounded-lg p-3">
                    <p className="text-lg font-semibold">{viewingProperty.size ?? '—'}</p>
                    <p className="text-xs text-muted-foreground">m²</p>
                  </div>
                </div>

                {/* Address */}
                <div>
                  <p className="text-sm font-medium mb-1">Address</p>
                  <p className="text-sm text-muted-foreground">
                    {[viewingProperty.address, viewingProperty.city, viewingProperty.state, viewingProperty.zipCode].filter(Boolean).join(', ') || '—'}
                  </p>
                </div>

                {/* Description */}
                {viewingProperty.description && (
                  <div>
                    <p className="text-sm font-medium mb-1">Description</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{viewingProperty.description}</p>
                  </div>
                )}

                {/* Features */}
                {features.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Features</p>
                    <div className="flex flex-wrap gap-2">
                      {features.map((f, i) => (
                        <Badge key={i} variant="outline" className="text-xs">{f}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rejection reason if rejected */}
                {viewingProperty.rejectionReason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-red-800 mb-1">Rejection Reason</p>
                    <p className="text-sm text-red-700">{viewingProperty.rejectionReason}</p>
                  </div>
                )}

                {/* Submitted date */}
                <p className="text-xs text-muted-foreground">
                  Submitted: {viewingProperty.createdAt ? new Date(viewingProperty.createdAt).toLocaleString() : '—'}
                </p>

                {/* Action buttons for pending properties */}
                {viewingProperty.status === 'pending' && (
                  <div className="flex gap-3 pt-2 border-t">
                    <Button
                      className="flex-1"
                      onClick={() => {
                        if (window.confirm('Approve this property listing?')) {
                          handleApproveProperty(viewingProperty.id);
                          setViewingProperty(null);
                        }
                      }}
                      data-testid="admin-sellers-property-detail-approve"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setViewingProperty(null);
                        handleRejectProperty(viewingProperty.id);
                      }}
                      data-testid="admin-sellers-property-detail-reject"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}